/*
 * KR Maps — Make your map (rendered by snippets/kr-make-your-map.liquid)
 *
 * The live preview for the Desk Size push-pin world map: the buyer's title, legend, names and date drawn
 * over the real basemap of the Map Style they picked, at the Size and Frame they picked, where Kevin's PSDs
 * put them. Everything is drawn in the PSDs' own pixel space (12000 x 9600 = 20 x 16 in at 600 ppi), so
 * the numbers below are read straight off Z:\MastersKevin\DeskSize\*.psd and the SVG scales to any width
 * without the type drifting. The basemaps (assets/kr-mym-<style>.jpg, and -full.jpg for a map with no
 * text, stretched the way Kevin stretched Neutralstretched.png) were composited from the same PSDs with
 * every type layer hidden.
 *
 * The visible inputs carry the properties[] names themselves (form="product-form-…"), so whatever the
 * buyer types reaches the order even if this script never runs. This script only:
 *   - draws the preview and keeps it in step with the variant picker,
 *   - switches every part off for Skip personalization (disabled inputs are never submitted),
 *   - turns on properties[Personalization] = None when nothing at all will print,
 *   - holds back blank properties at submit, so the order lists only what prints.
 */
(function () {
  'use strict';
  if (window.KRMakeYourMap) return;

  var SVGNS = 'http://www.w3.org/2000/svg';

  /* ── The master. 11 x 14 prints the same file at 14 x 11 in, so it is the same drawing 1.8 % shorter. ── */
  var MW = 12000, MH = 9600;
  var CX = 5995;                                   // centre line of the inner rule
  var SIZES = {
    s20: { label: '20 × 16 in', inW: 20, inH: 16 },
    s14: { label: '14 × 11 in', inW: 14, inH: 11 },
    s2416: { label: '24 × 16 in', inW: 24, inH: 16 },     // the 24 x 36 maps' two sizes, both 3:2 (one drawing)
    s3624: { label: '24 × 36 in', inW: 36, inH: 24 }       // labelled as the listing names it; drawn landscape
  };
  var FRAME_FACE_IN = 0.6;                          // white oak / dark oak moulding face
  var PX_IN = 96;                                   // the frame is drawn in CSS px per inch

  /* ── Type, from the PSD type layers (size in master px, tracking in em) ── */
  var TYPE = {
    /* title: the PSDs say tracking 120, but the Playfair that set Megan's orders spaces every gap 0.045 em
       wider than the web font does (same letter shapes, measured gap by gap on her orders), and Kevin
       follows her orders: 0.165. names: Adobe Garamond on paper, EB Garamond here, matched at 0.30 */
    title:    { family: "'Playfair Display', Georgia, serif", size: 517, track: 0.165, weight: 400, fit: 10600, dx: 22, vscale: 1.04 },
    subtitle: { family: "'Josefin Sans', 'Work Sans', sans-serif", size: 150, track: 0.20, weight: 400, fit: 10200, vscale: 1.04 },
    legend:   { family: "'Josefin Sans', 'Work Sans', sans-serif", size: 108.33, track: 0.20, weight: 400, vscale: 1.04 },
    names:    { family: "'EB Garamond', 'Cormorant Garamond', Georgia, serif", size: 275, track: 0.30, weight: 400, fit: 10200, vscale: 1.04 },
    date:     { family: "'Josefin Sans', 'Work Sans', sans-serif", size: 150, track: 0.20, weight: 400, fit: 10200, vscale: 1.04 }
  };

  /* ── Where things sit (master px): baselines for type, centres for ornaments ── */
  /* Megan's spacing from the recent orders (Kevin's call, 16 Oct 2026): about 280 px of air either side of the
     divider. The masters had 1041 / 1263 / 1574. Each style's own medians are in assets/kr-mym-styles.js. */
  var TOP = {
    full:  { title: 983, divider: 1263, subtitle: 1648 },
    alone: { title: 1082, divider: 1479.5 }           // no subtitle: the pair drops into the band
  };
  var DIVIDER = { cx: CX + 17, half: 1551.5, stroke: 13, dw: 63, dh: 63.5 };
  /* the legend hugs its divider as in the PSD: item 1 ends 323 short of it, item 2's ring starts 237 past it;
     with three items the middle one is centred and the others sit 740 off either side */
  var LEGEND = { cx: CX - 37, ring: 163.5, ringStroke: 20.3, gapText: 80, hugL: 323, hugR: 237, gap3: 740, fit: 10400, min: 0.6,
                 vUp: 360, vDown: 18, vW: 9, base: 44.5 };
  var RULE = { cx: CX - 33, w: 9, half: 1813, gap: 252, max: 5000, share: 0.88 };
  var COMPASS = 264;
  /* the bottom stack: each part's centre and its reach above / below, as in the full layout */
  var STACK = {
    legend2: { c: 8060, up: 360, down: 82 },         // the 2-item divider rises 360 above the row
    legend3: { c: 8060, up: 82, down: 82 },
    compass: { c: 8298, up: 132, down: 132 },
    names:   { c: 8615.5, up: 97.5, down: 97.5, base: 97.5 },   // baseline = centre + 97.5
    date:    { c: 8955, up: 61, down: 61, base: 61 }
  };
  /* nolegend_lift: with no legend, how far the rule, names and date sit above their places under one (bottomPlan) */
  var GAP = { legend_compass: 238, compass_names: 317.5, names_date: 339.5, compass_date: 300, nolegend_lift: 120 };
  var FIT_MIN = 0.6;
  /* Nothing at the top but words below: the empty title band would leave a wide strip of bare paper over the map
     (Kevin, 16 Oct 2026: stretch it up enough to balance it, nothing extreme). Megan's orders without a title do it
     by hand, the map layer stretched up from the bottom rule, which stays put: 9.6 % and 6.6 % on her two recent
     ones with a legend, names and date (none on title-only orders). So the map inside the inner rule is stretched
     up from y1 until its top edge (from, in every style's basemap) reaches to: 8.3 % taller, between those two,
     and the bottom block keeps its place. The inner rule is the same in every style (strokes 353-367 and 9221-9235
     down, 338-352 and 11637-11651 across) and stays as the basemap draws it: y0 and y1 are the first rows inside
     its strokes, x0 and x1 sit mid-stroke, where the stretch leaves the upright strokes as they are, so no seam shows. */
  var LIFT = { from: 1960, to: 1360, x0: 345, x1: 11644, y0: 368, y1: 9221 };

  /* ── Inks, read off each style PSD's own composite (Dark Forest's include its 15 % multiply wash).
        paper = the map's margin, shown while the basemap loads. ── */
  var STYLES = {
    forest:     { paper: '#95956d', title: '#fbeed9', sub: '#efce8b', divider: '#fbeed9', legend: '#fbeed9', ring: '#efce8b', rule: '#efce8b', names: '#fbeed9', date: '#efce8b' },
    darkforest: { paper: '#525237', title: '#eedec7', sub: '#e2c080', divider: '#eedec7', legend: '#eedec7', ring: '#e2c080', rule: '#e2c080', names: '#eedec7', date: '#e2c080' },
    sage:       { paper: '#f5ebd9', title: '#2f3a2f', sub: '#c2a46d', divider: '#5f6b5c', legend: '#2f3a2f', ring: '#aa8a3c', rule: '#5f6b5c', names: '#2f3a2f', date: '#c2a46d' },
    navy:       { paper: '#587884', title: '#f2e9da', sub: '#d7b45e', divider: '#d7b45e', legend: '#e5d9cb', ring: '#d7b45e', rule: '#d7b45e', names: '#f2e9da', date: '#d7b45e' },
    terracotta: { paper: '#f8f1e2', title: '#414449', sub: '#ce633d', divider: '#aa8a3c', legend: '#414449', ring: '#ce633d', rule: '#aa8a3c', names: '#414449', date: '#ce633d' },
    neutral:    { paper: '#fdf2ea', title: '#5a554a', sub: '#ae927a', divider: '#7a7468', legend: '#414449', ring: '#ae927a', rule: '#7a7468', names: '#5a554a', date: '#ae927a' },
    /* the April 2026 master (push-pin world map with pins): one style, its own layout (APRIL below) */
    april:      { paper: '#f8f5f0', title: '#334155', script: '#334155', names: '#334155', date: '#334155', border: '#3e4046' },
    /* the Muted Tones USA and world masters: the badge is the layout groups' colour overlay (BADGE below) */
    mutedusa:   { paper: '#f7f1eb', panel: '#f7f1eb', badge: '#406174' },
    mutedworld: { paper: '#f6f1eb', panel: '#f7f1eb', badge: '#406174' }
  };

  /* ── The April 2026 master, Z:\MastersKevin\DeskSize\April2026Master_2.psd: a title in Montserrat Light caps
        whose one chosen word is set in Dancing Script, on a paper band that breaks the border; names and a date
        or message below it. Read off the PSD's layers and composite (master px). ── */
  var APRIL = {
    /* the PSD says Montserrat Light 517 / 500 / 175 at 104 % tall, but the Montserrat Light Photoshop set it in is
       an older, heavier cut: its stems match Google's weight 400, and its letters run about 3 % wider and 5 % taller
       than Google's at the same size. Sizes and height scale are matched to the PSD's ink (within 2 px at 3000). */
    TYPE: {
      caps:   { family: "'Montserrat', 'Work Sans', sans-serif", size: 534, track: 0, weight: 400, vscale: 1.05 },
      script: { family: "'Dancing Script', cursive", size: 780, track: 0, weight: 400, vscale: 1.0635 },   // PSD 790 / 1.05
      names:  { family: "'Montserrat', 'Work Sans', sans-serif", size: 517.2, track: 0, weight: 400, fit: 10400, vscale: 1.056, dx: -12.5 },
      date:   { family: "'Montserrat', 'Work Sans', sans-serif", size: 181.9, track: 0, weight: 400, fit: 10400, vscale: 1.056, dx: -10.5 }
    },
    /* the title row centred on cx as one group; gapL / gapR sit either side of the script word; the band runs
       padL / padR past the row (the PSD's band isn't centred on it) */
    TITLE: { y: 1025, scriptY: 1062, cx: 5989.5, gapL: 159, gapR: 174, fit: 9600, min: 0.6, padL: 455, padR: 411 },
    BORDER: { left: 270, right: 11711, top: 802, bottom: 9293, w: 25 },   // centre lines of the 25 px stroke
    BOTTOM: { names: 8644, date: 9015 }                                     // baselines
  };
  /* ── The Muted Tones USA master, Z:\MastersMegan\Muted_Tones_USA.psd: 10800 x 7200 (36 x 24 in at 300 ppi), its own
        board. The words go in a badge on the map: the names on a ribbon, a date under them in the hexagon, up to five
        legend lines with rings, or instead a quote in its frame, all in Bebas Kai capitals. Each layout is one of the
        PSD's (names only, one to five lines, quote), picked by what is filled in: its art is a mask
        (assets/kr-mym-<style>-art-<layout>.png, from the tools' muted_bases.py) tinted with the layout group's colour
        overlay at the group's 80 %, and every line sits where Photoshop set it (muted_spec.py). The PSD set the date
        in Steelworks paint; Kevin chose Bebas Kai for it (17 Oct 2026), at the same cap height. The Muted Tones world
        map (style mutedworld) is the same badge on its own master (WORLD below). ── */
  var BADGE = {
    W: 10800, opacity: 0.8,
    cap: 0.7,                                   // Bebas Kai's capitals are 0.7 em tall (a shrunk line keeps its centre by them, setType)
    family: "'Bebas Kai', 'Bebas Neue', 'Oswald', sans-serif",
    /* art: the mask's box; cx: the badge's centre line; names / date / legend / quote: size in px, baseline y, and the
       widest a line may run before it shrinks (between the ribbon's tails, inside the frame); legend.ring: the art's
       rings, [left, right, [top of each]] (square, measured from the -lg art), which move over with short lines */
    LAYOUTS: {
      names: { art: [6504, 5930, 7785, 6633], panel: [6503, 6181, 7786, 6382], cx: 7144.5, names: { size: 168.02, y: 6341.6, fit: 1075 }, date: { size: 52.9, y: 6445 } },
      one:   { art: [6462, 5807, 7671, 6686], panel: [6582, 5948, 7555, 6687], cx: 7066.5, names: { size: 158.5, y: 6195.9, fit: 1013 }, date: { size: 48.6, y: 6294 },
               legend: { size: 95.1, x: 6799, y: [6594.6], fit: 732, ring: [6703, 6768, [6531]] } },
      two:   { art: [6510, 5763, 7623, 6702], panel: [6621, 5892, 7516, 6703], cx: 7066.5, names: { size: 145.83, y: 6122.1, fit: 927 }, date: { size: 45.7, y: 6211 },
               legend: { size: 87.5, x: 6862, y: [6487.2, 6608.2], fit: 621, ring: [6779, 6839, [6429, 6550]] } },
      three: { art: [6583, 5710, 7701, 6831], panel: [6694, 5840, 7590, 6832], cx: 7142, names: { size: 146.41, y: 6069.5, fit: 930 }, date: { size: 45.7, y: 6160 },
               legend: { size: 75.3, x: 6845, y: [6469.7, 6595.7, 6720.7], fit: 716, ring: [6764, 6820, [6415, 6541, 6669]] } },
      four:  { art: [6697, 5733, 7730, 6849], cx: 7213.5, names: { size: 135.18, y: 6065.6, fit: 855 }, date: { size: 42.9, y: 6149 },
               legend: { size: 69.52, x: 6936, y: [6410.7, 6526.7, 6642.7, 6757.7], fit: 654, ring: [6864, 6917, [6360, 6477, 6595, 6709]] } },
      five:  { art: [1687, 4725, 2664, 5865], cx: 2175.5, names: { size: 127.9, y: 5036.5, fit: 805 }, date: { size: 40, y: 5122 },
               legend: { size: 65.78, x: 1990, y: [5353, 5462, 5572, 5682, 5792], fit: 534, ring: [1921, 1971, [5304, 5414, 5526, 5635, 5746]] } },
      quote: { art: [6417, 5968, 7872, 6544], panel: [6416, 6000, 7874, 6545], cx: 7144.5, quote: { lines: [{ size: 120.85, y: 6194.6 }, { size: 120.85, y: 6327.6 }, { size: 85.76, y: 6460 }], fit: 1095 } }
    },
    /* the Muted Tones world master, Z:\MastersMegan\Muted_Tones_24x36_MasterNew.psd (Megan's world orders are made from
       it: their maps match it pixel for pixel): the same seven groups, art and colour, all on the lower left over the
       South Pacific. No paper panels: no grid line crosses there. Its names-only group keeps the date switched off and
       Megan switches it on when an order has one, so a date goes where that hidden layer sits. */
    WORLD: {
      names: { art: [1429, 4894, 2710, 5597], cx: 2069.5, names: { size: 170.84, y: 5307.6, fit: 1075 }, date: { size: 51.4, y: 5409 } },
      one:   { art: [1474, 4802, 2682, 5684], cx: 2078, names: { size: 158.5, y: 5192.9, fit: 1012 }, date: { size: 48.6, y: 5289 },
               legend: { size: 95.1, x: 1810, y: [5589.6], fit: 732, ring: [1715, 1779, [5527]] } },
      two:   { art: [1568, 4816, 2680, 5754], cx: 2124, names: { size: 166.67, y: 5179.7, fit: 926 }, date: { size: 44.3, y: 5263 },
               legend: { size: 87.5, x: 1919, y: [5539.2, 5660.2], fit: 621, ring: [1837, 1896, [5482, 5602]] } },
      three: { art: [1546, 4750, 2583, 5790], cx: 2064.5, names: { size: 135.99, y: 5084.2, fit: 859 }, date: { size: 42.9, y: 5167 },
               legend: { size: 69.94, x: 1788, y: [5456, 5573, 5689], fit: 655, ring: [1714, 1765, [5405, 5521, 5640]] } },
      four:  { art: [1531, 4711, 2562, 5826], cx: 2046.5, names: { size: 135.18, y: 5041.6, fit: 855 }, date: { size: 42.9, y: 5126 },
               legend: { size: 69.52, x: 1770, y: [5389.7, 5504.7, 5620.7, 5736.7], fit: 652, ring: [1698, 1749, [5338, 5454, 5572, 5687]] } },
      five:  { art: [1688, 4725, 2663, 5864], cx: 2175.5, names: { size: 127.9, y: 5035.5, fit: 805 }, date: { size: 40, y: 5122 },
               legend: { size: 65.78, x: 1990, y: [5354, 5463, 5573, 5682, 5792], fit: 533, ring: [1922, 1970, [5305, 5415, 5527, 5635, 5747]] } },
      quote: { art: [1261, 4965, 2715, 5540], cx: 1988, quote: { lines: [{ size: 120.85, y: 5190.6 }, { size: 120.85, y: 5323.6 }, { size: 85.76, y: 5456 }], fit: 1094 } }
    },
    /* the map sharp around the badge (assets/kr-mym-<style>-detail.jpg, 0.75 of the master, from the tools'
       muted_bases.py), laid over the 1600 px basemap: the box every layout's zoom and Legend close-up can show, so
       neither shows the soft map (Kevin, 18 Oct 2026: "is there a way we can prevent the legend closeup from having such
       soft rest of the map"). About 6 % of the map; the rest stays 1600 px across, as his copy rule has it. A layout
       moved by the style table needs this box worked out again. */
    DETAIL: { mutedusa: [5900, 5440, 8480, 7140], mutedworld: [800, 4440, 3440, 6140] }
  };
  var BADGE_KEYS = ['names', 'one', 'two', 'three', 'four', 'five'];
  /* which of the PSD's layouts a state draws ('' for none): a quote if one is written, else by the legend lines */
  function badgeLayout(st) {
    var s = shown(st);
    if (isBlank(s)) return '';
    return s.quote.some(Boolean) ? 'quote' : BADGE_KEYS[Math.min(5, s.items.length)];
  }
  function badgeGeo(style) {
    var o = shared(style).layout || {}, L = merge(style === 'mutedworld' ? BADGE.WORLD : BADGE.LAYOUTS, o.LAYOUTS);
    for (var k in L) if (L[k].at) L[k] = moveBadge(L[k], L[k].at);
    return { LAYOUTS: L };
  }
  /* a layout's at: [dx, dy] sets it elsewhere on the map: its art, panel and every line move together */
  function moveBadge(L, at) {
    var dx = +at[0] || 0, dy = +at[1] || 0, out = {}, k;
    var box = function (b) { return [b[0] + dx, b[1] + dy, b[2] + dx, b[3] + dy]; };
    var down = function (s) { return Object.assign({}, s, { y: s.y + dy }); };
    for (k in L) if (k !== 'at') out[k] = L[k];
    out.art = box(L.art);
    if (L.panel) out.panel = box(L.panel);
    out.cx = L.cx + dx;
    if (L.names) out.names = down(L.names);
    if (L.date) out.date = down(L.date);
    if (L.legend) out.legend = Object.assign({}, L.legend, { x: L.legend.x + dx, y: L.legend.y.map(function (y) { return y + dy; }) },
      L.legend.ring ? { ring: [L.legend.ring[0] + dx, L.legend.ring[1] + dx, L.legend.ring[2].map(function (y) { return y + dy; })] } : null);
    if (L.quote) out.quote = Object.assign({}, L.quote, { lines: L.quote.lines.map(down) });
    return out;
  }
  function kindOf(style) { return shared(style).kind || (style === 'april' ? 'april' : /^muted/.test(style || '') ? 'badge' : 'desk'); }
  function aprilGeo(style) {
    var o = shared(style).layout || {};
    return { TYPE: merge(APRIL.TYPE, o.TYPE), TITLE: merge(APRIL.TITLE, o.TITLE), BORDER: merge(APRIL.BORDER, o.BORDER), BOTTOM: merge(APRIL.BOTTOM, o.BOTTOM) };
  }
  var FRAME_NAMES = { none: 'No frame', white: 'White oak frame', dark: 'Dark oak frame' };

  /* ── assets/kr-mym-styles.js (window.KRMymStyles) is the one style table shared with the print files and
        the Etsy proof desk: its inks and its per-style layout overrides are laid over the defaults above,
        looked up at draw time, so a fix there reaches every builder. Without it the built-in table draws. ── */
  function shared(style) {
    var t = window.KRMymStyles && window.KRMymStyles.styles;
    return (t && t[style]) || {};
  }
  function isPlain(o) { return o && typeof o === 'object' && !Array.isArray(o); }
  function merge(base, over) {
    if (!isPlain(over)) return base;
    var out = {};
    for (var k in base) out[k] = isPlain(base[k]) ? merge(base[k], {}) : base[k];
    for (var j in over) out[j] = isPlain(over[j]) && isPlain(base[j]) ? merge(base[j], over[j]) : over[j];
    return out;
  }
  function inksFor(style) { return merge(STYLES[style] || STYLES.neutral, shared(style).inks); }
  function geo(style) {
    var o = shared(style).layout || {};
    var g = {
      TYPE: merge(TYPE, o.TYPE), TOP: merge(TOP, o.TOP), DIVIDER: merge(DIVIDER, o.DIVIDER), LEGEND: merge(LEGEND, o.LEGEND),
      RULE: merge(RULE, o.RULE), STACK: merge(STACK, o.STACK), GAP: merge(GAP, o.GAP), LIFT: merge(LIFT, o.LIFT)
    };
    /* the 2-item stack reaches as high as its divider rises, unless the override says otherwise */
    if (!(o.STACK && o.STACK.legend2 && o.STACK.legend2.up != null)) g.STACK.legend2.up = g.LEGEND.vUp;
    return g;
  }

  var PARTS = ['title', 'subtitle', 'legend', 'names', 'dateline', 'quote'];
  var TEXTS = ['title', 'subtitle', 'names', 'dateline', 'notes', 'quote1', 'quote2', 'quoteBy'];
  var LIMITS = { title: 24, subtitle: 40, legend: 22, names: 28, dateline: 34 };
  var LIMITS_APRIL = { title: 32, names: 26, dateline: 48 };          // past these a line prints smaller to fit
  /* the badge's, from Bebas Kai's widths in its tightest layout (names 56 px a letter on two lines, the legend 33, the quote 40) */
  var LIMITS_BADGE = { names: 16, dateline: 20, legend: 18, quote1: 26, quote2: 26, quoteBy: 30 };
  /* the badge's layouts as the order names them (Customily's choices, plus names and date only) */
  var BADGE_NAMES = { names: 'Names and date', one: 'Name and One', two: 'Name and Two', three: 'Name and Three', four: 'Name and Four',
                      five: 'Names and Five', quote: 'Quote' };

  /* ───────────────────────────── helpers ───────────────────────────── */

  var uidCounter = 0;
  function el(tag, attrs, kids) {
    var n = document.createElementNS(SVGNS, tag);
    if (attrs) for (var k in attrs) if (attrs[k] != null) n.setAttribute(k, attrs[k]);
    if (kids) kids.forEach(function (c) { if (c) n.appendChild(c); });
    return n;
  }
  function setKids(node, list) {                       // replaceChildren, for browsers that predate it
    if (node.replaceChildren) return node.replaceChildren.apply(node, list);
    while (node.firstChild) node.removeChild(node.firstChild);
    list.forEach(function (c) { node.appendChild(c); });
  }
  function trim(s) { return String(s || '').replace(/\s+/g, ' ').trim(); }
  function caps(s) { return trim(s).toUpperCase(); }
  function styleKey(v) {
    var k = String(v || '').toLowerCase().replace(/[^a-z]/g, '');
    return STYLES[k] || shared(k).inks ? k : 'neutral';
  }
  function sizeKey(v) {
    var s = String(v || '');
    if (/\b(24\s*x\s*16|16\s*x\s*24)\b/i.test(s)) return 's2416';
    if (/\b(24\s*x\s*36|36\s*x\s*24)\b/i.test(s)) return 's3624';
    return /11/.test(s) && /14/.test(s) ? 's14' : 's20';
  }
  function frameKey(v) { var s = String(v || '').toLowerCase(); return /white/.test(s) ? 'white' : /dark/.test(s) ? 'dark' : 'none'; }
  function boardH(size) { var z = SIZES[size] || SIZES.s20; return MW * z.inH / z.inW; }   // 9600, or 9428.6 at 14 x 11
  /* the board in master px: 12000 across for the Desk Size and April masters, the Muted Tones masters' own 10800 */
  function boardOf(st) {
    var z = SIZES[st.size] || SIZES.s20, w = kindOf(st.style) === 'badge' ? BADGE.W : MW;
    return { W: w, H: w * z.inH / z.inW };
  }
  var reduceMotion = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  /* What actually prints: a part must be switched on and have words; a subtitle also needs the title. The Muted
     Tones badge holds either names, a date and legend lines, or a quote (st.quoteMode) in its three lines. */
  function shown(st) {
    var inc = st.include, q = !!st.quoteMode;
    var title = inc.title ? trim(st.title) : '';
    return {
      title: title,
      subtitle: title && inc.subtitle ? trim(st.subtitle) : '',
      items: !q && inc.legend ? st.legend.slice(0, st.legendCount).map(trim).filter(Boolean) : [],
      names: !q && inc.names ? trim(st.names) : '',
      dateline: !q && inc.dateline ? trim(st.dateline) : '',
      script: title ? trim(st.script) : '',                   // the April layout's one word in script
      quote: q && inc.quote !== false ? [trim(st.quote1), trim(st.quote2), trim(st.quoteBy)] : []
    };
  }
  function isBlank(s) { return !s.title && !s.items.length && !s.names && !s.dateline && !s.quote.some(Boolean); }

  /* ───────────────────────────── the board ───────────────────────────── */

  /* A line of type centred on the inner rule. letter-spacing also follows the last letter, so the anchor
     moves right by half of it to centre the ink itself. */
  function line(text, spec, y, fill) {
    var ls = spec.size * spec.track;
    var t = el('text', {
      x: (CX + (spec.dx || 0) + ls / 2).toFixed(1), y: y.toFixed(1), 'text-anchor': 'middle', fill: fill,
      'font-family': spec.family, 'font-size': spec.size, 'font-weight': spec.weight, 'letter-spacing': ls.toFixed(1),
      'data-size': spec.size, 'data-track': spec.track, 'data-fit': spec.fit, 'data-dx': spec.dx || 0,
      transform: vscale(spec, y)
    });
    t.textContent = caps(text);
    return t;
  }
  /* every type layer in Kevin's PSDs is set at Vertically Scale 104 %: taller letters on the same baseline */
  function vscale(spec, y) {
    var v = spec.vscale == null ? 1 : +spec.vscale;
    return v === 1 ? null : 'matrix(1 0 0 ' + v + ' 0 ' + (-(v - 1) * y).toFixed(2) + ')';
  }

  function compass(cx, cy, color, url, uid) {
    var x = cx - COMPASS / 2, y = cy - COMPASS / 2;
    return el('g', null, [
      el('mask', { id: uid + 'cm', maskUnits: 'userSpaceOnUse', x: x, y: y, width: COMPASS, height: COMPASS }, [
        el('image', { href: url || '', x: x, y: y, width: COMPASS, height: COMPASS })
      ]),
      el('rect', { x: x, y: y, width: COMPASS, height: COMPASS, fill: color, mask: 'url(#' + uid + 'cm)' })
    ]);
  }

  /* The bottom block, top to bottom: legend row, the rule split by the compass, names, date line.
     A part left out closes the stack up, and the shorter stack stays centred where the full block sits. With no
     legend, though, the rule, the names and the date sit just above the places they have under one (GAP.nolegend_lift):
     centred, they rose by half the legend's height, and on Navy's tall legend divider by 467 px (Kevin, Oct 26, 2026:
     "no legend option is too high up"; then, from their places under a legend, "up just a tad"). */
  function bottomPlan(s, g) {
    var STACK = g.STACK, GAP = g.GAP;
    var seq = [], legKey = s.items.length === 3 ? 'legend3' : 'legend2';
    if (s.items.length) seq.push({ k: 'legend', m: STACK[legKey] });
    if (s.items.length || s.names || s.dateline) seq.push({ k: 'compass', m: STACK.compass });
    if (s.names) seq.push({ k: 'names', m: STACK.names });
    if (s.dateline) seq.push({ k: 'date', m: STACK.date });
    if (!seq.length) return seq;
    if (seq.length === 4) { seq.forEach(function (p) { p.c = p.m.c; }); return seq; }
    var gapOf = function (a, b) {
      if (a === 'legend') return GAP.legend_compass;
      if (a === 'compass') return b === 'names' ? GAP.compass_names : GAP.compass_date;
      return GAP.names_date;
    };
    var fullTop = STACK[legKey].c - STACK[legKey].up, fullBottom = STACK.date.c + STACK.date.down;
    var c = 0;
    seq.forEach(function (p, i) { if (i) c += gapOf(seq[i - 1].k, p.k); p.c = c; });
    if (!s.items.length) { seq.forEach(function (p) { p.c += STACK.compass.c - (GAP.nolegend_lift || 0); }); return seq; }
    var top = -seq[0].m.up, bottom = c + seq[seq.length - 1].m.down;
    var shift = (fullTop + fullBottom) / 2 - (top + bottom) / 2;
    seq.forEach(function (p) { p.c += shift; });
    return seq;
  }

  /* Everything printed over the basemap, for one state. The style's own geometry (defaults + shared overrides)
     shadows the module constants here. Optional inks: vline (legend divider), compass and diamond fall back
     to rule, rule and divider. */
  /* The April layout: the border (its top in two runs, spaced by layout() around the title), the title row in
     up to three parts, the names and the date. A script word that isn't one of the title's words is ignored. */
  function scriptIndex(words, script) {
    var w = String(script || '').toLowerCase();
    if (!w) return -1;
    for (var i = 0; i < words.length; i++) if (words[i].toLowerCase() === w) return i;
    return -1;
  }
  /* a word typed all in capitals or all in small letters is set as World; one already mixed (McAllister) stays */
  function scriptCase(w) {
    return w === w.toUpperCase() || w === w.toLowerCase() ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : w;
  }
  function part(text, spec, y, fill, name) {
    var t = el('text', { x: 0, y: y.toFixed(1), 'text-anchor': 'start', fill: fill, 'font-family': spec.family, 'font-size': spec.size,
                         'font-weight': spec.weight, 'letter-spacing': (spec.size * spec.track).toFixed(1), 'data-part': name,
                         'data-size': spec.size, transform: vscale(spec, y) });
    t.textContent = text;
    return t;
  }
  function ornamentsApril(st, opts) {
    var A = aprilGeo(st.style), T = A.TYPE, B = A.BORDER, s = shown(st), ink = inksFor(st.style), ky = boardH(st.size) / MH, out = [];
    var top = B.top * ky, bottom = B.bottom * ky, h = B.w / 2, span = B.right - B.left + B.w;
    out.push(el('g', { 'data-mym-border': '', fill: ink.border }, [
      el('rect', { x: B.left - h, y: (top - h).toFixed(1), width: B.w, height: (bottom - top + B.w).toFixed(1) }),
      el('rect', { x: B.right - h, y: (top - h).toFixed(1), width: B.w, height: (bottom - top + B.w).toFixed(1) }),
      el('rect', { x: B.left - h, y: (bottom - h).toFixed(1), width: span, height: B.w }),
      el('rect', { 'data-seg': 'tl', x: B.left - h, y: (top - h).toFixed(1), width: span, height: B.w }),
      el('rect', { 'data-seg': 'tr', x: B.right + h, y: (top - h).toFixed(1), width: 0, height: B.w })
    ]));
    if (s.title) {
      var words = s.title.split(' '), k = scriptIndex(words, s.script), y = A.TITLE.y * ky;
      var row = el('g', { 'data-mym-title': '', 'data-band': 'top' });
      if (k < 0) {
        row.appendChild(part(caps(s.title), T.caps, y, ink.title, 'l'));
      } else {
        if (k > 0) row.appendChild(part(caps(words.slice(0, k).join(' ')), T.caps, y, ink.title, 'l'));
        row.appendChild(part(scriptCase(words[k]), T.script, A.TITLE.scriptY * ky, ink.script || ink.title, 's'));
        if (k < words.length - 1) row.appendChild(part(caps(words.slice(k + 1).join(' ')), T.caps, y, ink.title, 'r'));
      }
      out.push(row);
    }
    if (s.names) out.push(line(s.names, T.names, A.BOTTOM.names * ky, ink.names));
    if (s.dateline) out.push(line(s.dateline, T.date, A.BOTTOM.date * ky, ink.date));
    return out;
  }
  /* laid out once the fonts are in: the row centred as one group (shrunk to fit), the band a pad either side */
  function layoutApril(svg, A) {
    var tl = svg.querySelector('[data-mym-border] [data-seg="tl"]'), tr = svg.querySelector('[data-mym-border] [data-seg="tr"]');
    var B = A.BORDER, T = A.TITLE, h = B.w / 2, row = svg.querySelector('[data-mym-title]');
    if (!tl || !tr) return;
    if (!row) { tl.setAttribute('width', B.right - B.left + B.w); tr.setAttribute('width', 0); return; }
    var p = { l: row.querySelector('[data-part="l"]'), s: row.querySelector('[data-part="s"]'), r: row.querySelector('[data-part="r"]') };
    var k = 1, w = {}, total = 0, gl = 0, gr = 0;
    function measure() {
      ['l', 's', 'r'].forEach(function (n) { w[n] = p[n] ? inkWidth(p[n], 0) : 0; });
      gl = p.s && p.l ? T.gapL * k : 0; gr = p.s && p.r ? T.gapR * k : 0;
      total = w.l + gl + w.s + gr + w.r;
    }
    ['l', 's', 'r'].forEach(function (n) { if (p[n]) p[n].setAttribute('font-size', (+p[n].getAttribute('data-size')).toFixed(1)); });
    measure();
    if (total > T.fit) {
      k = Math.max(T.min, T.fit / total);
      ['l', 's', 'r'].forEach(function (n) { if (p[n]) p[n].setAttribute('font-size', (+p[n].getAttribute('data-size') * k).toFixed(1)); });
      measure();
    }
    var x = T.cx - total / 2;
    if (p.l) { p.l.setAttribute('x', x.toFixed(1)); x += w.l + gl; }
    if (p.s) { p.s.setAttribute('x', x.toFixed(1)); x += w.s + gr; }
    if (p.r) p.r.setAttribute('x', x.toFixed(1));
    var x0 = T.cx - total / 2 - T.padL * k, x1 = T.cx + total / 2 + T.padR * k;
    tl.setAttribute('width', Math.max(0, x0 - (B.left - h)).toFixed(1));
    tr.setAttribute('x', Math.min(B.right + h, x1).toFixed(1));
    tr.setAttribute('width', Math.max(0, B.right + h - x1).toFixed(1));
  }

  /* The Muted Tones badge: the layout's art and its lines, in one group at the group's 80 %, as the PSD composites
     it (art and words don't darken where they meet). A line too wide for its place shrinks (data-fit, in layout());
     the legend lines keep their left edge by the rings, and short ones move over with their rings to sit centred in
     the frame (layoutBadge). opts.badgeArt: the art images' URLs, by layout. */
  function badgeText(text, size, x, y, anchor, fill, fit, part) {
    var t = el('text', { x: x.toFixed(1), y: y.toFixed(1), 'text-anchor': anchor, fill: fill, 'font-family': BADGE.family, 'font-size': size,
                         'letter-spacing': 0, 'data-size': size, 'data-track': 0, 'data-fit': fit, 'data-cx': anchor === 'middle' ? x : null,
                         'data-part': part, 'data-band': 'bottom', 'data-y': y.toFixed(1) });     // data-y: the baseline at full size
    t.textContent = caps(text);
    return t;
  }
  function ornamentsBadge(st, opts, uid) {
    var key = badgeLayout(st);
    if (!key) return [];
    var L = badgeGeo(st.style).LAYOUTS[key], s = shown(st), inks = inksFor(st.style), ink = inks.badge || '#406174';
    var x = L.art[0], y = L.art[1], w = L.art[2] - L.art[0], h = L.art[3] - L.art[1], art = (opts.badgeArt || {})[key] || '';
    /* most layouts lay a plain paper panel (the PSD's 'Rectangle 1') under the frame, so the map's lines stop at it */
    var panel = L.panel ? el('rect', { x: L.panel[0], y: L.panel[1], width: L.panel[2] - L.panel[0], height: L.panel[3] - L.panel[1],
                                       fill: inks.panel || '#f7f1eb', 'data-mym-panel': '' }) : null;
    /* the art comes tinted (the overlay colour, its coverage as alpha): a mask would turn grey edges to alpha in
       linear light and thin the rays */
    var img = el('image', { href: art, x: x, y: y, width: w, height: h, preserveAspectRatio: 'none', 'data-mym-art': '' });
    var g = el('g', { 'data-mym-badge': key, opacity: BADGE.opacity }, [img]);
    var out = panel ? [panel, g] : [g];
    /* the rings are in the art: it draws with a hole at each, and a copy of it (a <use>, so a swap of the art's href
       carries over) clipped to just the rings draws them where layoutBadge moves the lines */
    var R = key !== 'quote' && L.legend && L.legend.ring;
    if (R && s.items.length) {
      var d = R[2].map(function (t) { var a = R[1] - R[0]; return 'M' + R[0] + ' ' + t + 'h' + a + 'v' + a + 'h' + (-a) + 'Z'; }).join('');
      img.setAttribute('id', uid + 'art');
      g.appendChild(el('defs', null, [
        el('clipPath', { id: uid + 'bhole' }, [el('path', { d: 'M' + x + ' ' + y + 'h' + w + 'v' + h + 'h' + (-w) + 'Z' + d, 'clip-rule': 'evenodd' })]),
        el('clipPath', { id: uid + 'brings' }, [el('path', { d: d })])
      ]));
      g.insertBefore(el('g', { 'clip-path': 'url(#' + uid + 'bhole)', 'data-mym-art-hole': 'url(#' + uid + 'bhole)' }, [img]), g.firstChild);
      g.appendChild(el('g', { 'data-mym-rings': '', 'data-cx': L.cx, 'data-x0': R[0] }, [
        el('use', { href: '#' + uid + 'art', 'clip-path': 'url(#' + uid + 'brings)' })
      ]));
    }
    if (key === 'quote') {
      L.quote.lines.forEach(function (ln, i) { if (s.quote[i]) g.appendChild(badgeText(s.quote[i], ln.size, L.cx, ln.y, 'middle', ink, L.quote.fit, 'quote')); });
      return out;
    }
    if (s.names) g.appendChild(badgeText(s.names, L.names.size, L.cx, L.names.y, 'middle', ink, L.names.fit, 'names'));
    if (s.dateline) g.appendChild(badgeText(s.dateline, L.date.size, L.cx, L.date.y, 'middle', ink, L.names.fit * 0.4, 'dateline'));
    if (L.legend) s.items.forEach(function (item, i) {
      var t = badgeText(item, L.legend.size, L.legend.x, L.legend.y[i], 'start', ink, L.legend.fit, 'legend');
      t.setAttribute('data-lx', L.legend.x);           // the left edge the master gives the lines, before any move
      g.appendChild(t);
    });
    return out;
  }
  /* Short legend lines (Kevin, 28 Oct 2026: "if someone does short legend item words that it centers them better"):
     the lines stay left-aligned on each other, and the column (the rings to the end of the widest line, as fitted)
     moves right until it sits centred in the frame. A column already as wide as its place stays where the master has
     it, as does the order desk's two-column badge, which redraws the art itself (svg.__mym.wide). */
  function layoutBadge(svg) {
    var rg = svg.querySelector('[data-mym-rings]'), hole = svg.querySelector('[data-mym-art-hole]');
    if (!rg) return;
    var wide = !!(svg.__mym && svg.__mym.wide), lines = svg.querySelectorAll('[data-mym-badge] text[data-part="legend"]');
    if (hole) { if (wide) hole.removeAttribute('clip-path'); else hole.setAttribute('clip-path', hole.getAttribute('data-mym-art-hole')); }
    rg.style.display = wide ? 'none' : '';
    if (wide || !lines.length) return;
    var lx = +lines[0].getAttribute('data-lx'), right = 0;
    for (var i = 0; i < lines.length; i++) right = Math.max(right, inkWidth(lines[i], 0));
    var dx = Math.max(0, +rg.getAttribute('data-cx') - (+rg.getAttribute('data-x0') + lx + right) / 2);
    for (var j = 0; j < lines.length; j++) lines[j].setAttribute('x', (+lines[j].getAttribute('data-lx') + dx).toFixed(1));
    if (dx) rg.setAttribute('transform', 'translate(' + dx.toFixed(1) + ' 0)'); else rg.removeAttribute('transform');
  }

  function ornaments(st, opts, uid) {
    if (kindOf(st.style) === 'badge') return ornamentsBadge(st, opts, uid);
    if (kindOf(st.style) === 'april') return ornamentsApril(st, opts);
    var gm = geo(st.style), TYPE = gm.TYPE, TOP = gm.TOP, DIVIDER = gm.DIVIDER, LEGEND = gm.LEGEND, RULE = gm.RULE, STACK = gm.STACK;
    var s = shown(st), ink = inksFor(st.style), ky = boardH(st.size) / MH, out = [];
    if (s.title) {
      var tp = s.subtitle ? TOP.full : TOP.alone, dy = tp.divider * ky, dx = DIVIDER.cx;
      out.push(line(s.title, TYPE.title, tp.title * ky, ink.title));
      out.push(el('g', { 'data-band': 'top' }, [
        el('rect', { x: dx - DIVIDER.half, y: dy - DIVIDER.stroke / 2, width: DIVIDER.half * 2, height: DIVIDER.stroke, fill: ink.divider }),
        el('polygon', { points: [dx, dy - DIVIDER.dh, dx + DIVIDER.dw, dy, dx, dy + DIVIDER.dh, dx - DIVIDER.dw, dy].join(' '), fill: ink.diamond || ink.divider })
      ]));
      if (s.subtitle) out.push(line(s.subtitle, TYPE.subtitle, TOP.full.subtitle * ky, ink.sub));
    }
    bottomPlan(s, gm).forEach(function (p) {
      var cy = p.c * ky;
      if (p.k === 'legend') {
        var g = el('g', { 'data-mym-legend': s.items.length, 'data-cy': cy.toFixed(1), 'data-band': 'bottom' });
        if (s.items.length === 2) {
          g.appendChild(el('rect', { x: LEGEND.cx - LEGEND.vW / 2, y: (cy - LEGEND.vUp).toFixed(1), width: LEGEND.vW,
                                     height: LEGEND.vUp - LEGEND.vDown, fill: ink.vline || ink.rule }));
        }
        var spec = TYPE.legend, ls = spec.size * spec.track;
        s.items.forEach(function (txt) {
          var t = el('text', { x: 0, y: (cy + LEGEND.base).toFixed(1), 'text-anchor': 'start', fill: ink.legend, 'font-family': spec.family,
                               'font-size': spec.size, 'font-weight': spec.weight, 'letter-spacing': ls.toFixed(1),
                               transform: vscale(spec, cy + LEGEND.base) });
          t.textContent = caps(txt);
          g.appendChild(el('g', { 'data-item': '' }, [
            el('circle', { cx: 0, cy: cy.toFixed(1), r: ((LEGEND.ring - LEGEND.ringStroke) / 2).toFixed(1), fill: 'none',
                           stroke: ink.ring, 'stroke-width': LEGEND.ringStroke }),
            t
          ]));
        });
        out.push(g);
      } else if (p.k === 'compass') {
        out.push(el('g', { 'data-mym-rule': '', 'data-band': 'bottom' }, [
          el('rect', { 'data-seg': 'l', x: RULE.cx - RULE.half, y: (cy - RULE.w / 2).toFixed(1), width: RULE.half - RULE.gap, height: RULE.w, fill: ink.rule }),
          el('rect', { 'data-seg': 'r', x: RULE.cx + RULE.gap, y: (cy - RULE.w / 2).toFixed(1), width: RULE.half - RULE.gap, height: RULE.w, fill: ink.rule }),
          compass(RULE.cx, cy, ink.compass || ink.rule, opts.compass, uid)
        ]));
      } else if (p.k === 'names') {
        out.push(line(s.names, TYPE.names, cy + STACK.names.base * ky, ink.names));
      } else {
        out.push(line(s.dateline, TYPE.date, cy + STACK.date.base * ky, ink.date));
      }
    });
    return out;
  }

  function baseHref(st, opts) {
    var b = (opts.bases && opts.bases[st.style]) || {}, blank = isBlank(shown(st));
    return blank ? (opts.large ? b.fullLg || b.full : b.full) : (opts.large ? b.bandLg || b.band : b.band);
  }

  /* The lift (LIFT above) for one state, in board units, or null when the basemap is drawn as is. The lifted
     copy is clipped to x, y, width, height and stretched by k about the line y = fixed: y' = fixed - k (fixed - y).
     Only the Desk Size template lifts (the April map has its own). */
  function liftOf(st) {
    var s = shown(st);
    if (kindOf(st.style) !== 'desk' || s.title || isBlank(s)) return null;
    var L = geo(st.style).LIFT, ky = boardH(st.size) / MH;
    if (!(L.to < L.from)) return null;
    return { x: L.x0, y: L.y0 * ky, width: L.x1 - L.x0, height: (L.y1 - L.y0) * ky,
             k: (L.y1 - L.to) / (L.y1 - L.from), fixed: L.y1 * ky };
  }
  /* The same basemap drawn again over itself, lifted: a <use> of it, so it follows every href the base is given
     and draws nothing where a builder takes the base out of the SVG to paint it itself */
  function lift(svg, st, uid) {
    var board = svg.querySelector('[data-mym-board]'), g = board.querySelector('[data-mym-lift]');
    var base = board.querySelector('[data-mym-base]'), l = liftOf(st);
    svg.__mym.lift = l;
    if (!g) return;
    if (!l) { setKids(g, []); return; }
    setKids(g, [
      el('clipPath', { id: uid + 'lift' }, [
        el('rect', { x: l.x, y: l.y.toFixed(1), width: l.width, height: l.height.toFixed(1) })
      ]),
      el('g', { 'clip-path': 'url(#' + uid + 'lift)' }, [
        el('use', { href: '#' + base.id, transform: 'matrix(1 0 0 ' + l.k.toFixed(5) + ' 0 ' + (l.fixed * (1 - l.k)).toFixed(2) + ')' })
      ])
    ]);
  }

  /* ── the physical frame, drawn in inches x 96 around the board (tones sampled from the frame photos) ── */
  function frameSVG(OW, OH, F, kind, uid) {
    var c = kind === 'dark'
      ? { base: '#6a4323', band: '#4a2b13', bandO: .6, line: '#331d0c', lineO: .55, fleck: '#c08c50', fleckO: .55, seam: 'rgba(0,0,0,.5)', hi: 'rgba(255,214,160,.26)', lo: 'rgba(0,0,0,.5)' }
      : { base: '#cd9b60', band: '#b47c48', bandO: .55, line: '#9d6838', lineO: .5, fleck: '#f3d4a2', fleckO: .7, seam: 'rgba(80,45,10,.38)', hi: 'rgba(255,238,205,.6)', lo: 'rgba(80,50,20,.38)' };
    var id = uid + kind;
    function flt(name, bfx, bfy, oct, seed, k, b, blur) {
      /* the grain takes the rect's own colour, masked by the noise */
      return el('filter', { id: id + name, x: 0, y: 0, width: 1, height: 1, 'color-interpolation-filters': 'sRGB' }, [
        el('feTurbulence', { type: 'fractalNoise', baseFrequency: bfx + ' ' + bfy, numOctaves: oct, seed: seed, result: 'n' }),
        el('feColorMatrix', { 'in': 'n', type: 'matrix', values: '0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  ' + k + ' 0 0 0 ' + b, result: 'a' }),
        el('feGaussianBlur', { 'in': 'a', stdDeviation: blur, result: 'm' }),
        el('feComposite', { 'in': 'SourceGraphic', in2: 'm', operator: 'in' })
      ]);
    }
    function clip(name, pts) { return el('clipPath', { id: id + name }, [el('polygon', { points: pts })]); }
    var W = OW - 2 * F, H = OH - 2 * F;
    var defs = el('defs', null, [
      flt('bandh', 0.0022, 0.07, 3, 5, 3.0, -1.55, 0.8), flt('bandv', 0.07, 0.0022, 3, 6, 3.0, -1.55, 0.8),     // broad wavy bands
      flt('lineh', 0.0015, 0.42, 2, 7, 6.0, -3.4, 0.35), flt('linev', 0.42, 0.0015, 2, 8, 6.0, -3.4, 0.35),     // long fine grain
      flt('fleckh', 0.028, 0.2, 2, 9, 7.0, -4.2, 0.4), flt('fleckv', 0.2, 0.028, 2, 10, 7.0, -4.2, 0.4),        // short ray flecks
      clip('top', '0,0 ' + OW + ',0 ' + (OW - F) + ',' + F + ' ' + F + ',' + F),
      clip('bot', '0,' + OH + ' ' + OW + ',' + OH + ' ' + (OW - F) + ',' + (OH - F) + ' ' + F + ',' + (OH - F)),
      clip('left', '0,0 ' + F + ',' + F + ' ' + F + ',' + (OH - F) + ' 0,' + OH),
      clip('right', OW + ',0 ' + OW + ',' + OH + ' ' + (OW - F) + ',' + (OH - F) + ' ' + (OW - F) + ',' + F)
    ]);
    function piece(name, dir, light) {
      var r = function (fill, filter, op) { return el('rect', { width: OW, height: OH, fill: fill, filter: filter ? 'url(#' + id + filter + dir + ')' : null, opacity: op }); };
      return el('g', { 'clip-path': 'url(#' + id + name + ')' }, [r(c.base), r(c.band, 'band', c.bandO), r(c.line, 'line', c.lineO), r(c.fleck, 'fleck', c.fleckO), r(light)]);
    }
    var under = el('g', null, [
      defs, piece('top', 'h', 'rgba(255,245,225,.10)'), piece('bot', 'h', 'rgba(40,20,0,.14)'),
      piece('left', 'v', 'rgba(255,245,225,.04)'), piece('right', 'v', 'rgba(40,20,0,.07)'),
      el('g', { stroke: c.seam, 'stroke-width': 1 }, [
        el('line', { x1: 0, y1: 0, x2: F, y2: F }), el('line', { x1: OW, y1: 0, x2: OW - F, y2: F }),
        el('line', { x1: 0, y1: OH, x2: F, y2: OH - F }), el('line', { x1: OW, y1: OH, x2: OW - F, y2: OH - F })
      ]),
      el('rect', { x: 0.75, y: 0.75, width: OW - 1.5, height: OH - 1.5, fill: 'none', stroke: c.hi, 'stroke-width': 1.5 }),
      el('rect', { x: F - 1, y: F - 1, width: W + 2, height: H + 2, fill: 'none', stroke: c.lo, 'stroke-width': 2 })
    ]);
    var over = el('g', { 'pointer-events': 'none' }, [
      el('defs', null, [
        el('clipPath', { id: id + 'in' }, [el('rect', { x: F, y: F, width: W, height: H })]),
        el('filter', { id: id + 'sh' }, [el('feGaussianBlur', { stdDeviation: 3.5 })])
      ]),
      el('rect', { x: F, y: F, width: W, height: H, fill: 'none', stroke: 'rgba(30,18,8,.55)', 'stroke-width': 9,
                   filter: 'url(#' + id + 'sh)', 'clip-path': 'url(#' + id + 'in)' })   // the moulding's shadow on the print
    ]);
    return { under: under, over: over };
  }

  /* Build the whole drawing, the frame (if any) around the board. Returns the outer <svg>. */
  function build(st, opts) {
    var uid = 'kmym' + (++uidCounter) + '-';
    var z = SIZES[st.size] || SIZES.s20, D = boardOf(st), H = D.H;
    var W96 = z.inW * PX_IN, H96 = z.inH * PX_IN, F = st.frame === 'none' ? 0 : FRAME_FACE_IN * PX_IN;
    var OW = W96 + 2 * F, OH = H96 + 2 * F;
    var ink = inksFor(st.style), href = opts.href || baseHref(st, opts);
    /* a badge map's sharp patch: in place, empty until the page fetches it (opts.badgeDetail: its URL; the print has none) */
    var dbox = opts.badgeDetail && kindOf(st.style) === 'badge' && BADGE.DETAIL[st.style];
    var board = el('svg', { x: F, y: F, width: W96, height: H96, viewBox: '0 0 ' + D.W + ' ' + H.toFixed(1),
                            preserveAspectRatio: 'none', 'data-mym-board': '' }, [
      el('rect', { width: D.W, height: H.toFixed(1), fill: ink.paper, 'data-mym-paper': '' }),
      el('image', { id: uid + 'base', href: href || '', x: 0, y: 0, width: D.W, height: H.toFixed(1), preserveAspectRatio: 'none', 'data-mym-base': '' }),
      dbox ? el('image', { href: '', x: dbox[0], y: dbox[1], width: dbox[2] - dbox[0], height: dbox[3] - dbox[1], preserveAspectRatio: 'none',
                           'data-mym-detail': '' }) : null,
      el('g', { 'data-mym-lift': '' }),
      el('g', { 'data-mym-ink': '' }, ornaments(st, opts, uid))
    ]);
    var svg = el('svg', { viewBox: '0 0 ' + OW + ' ' + OH, width: '100%', role: 'img', focusable: 'false', 'class': 'kr-mym-svg',
                          'aria-label': opts.label || 'Preview of your map', 'data-mym-style': st.style });
    svg.style.aspectRatio = OW + ' / ' + OH;
    if (F) {
      var fr = frameSVG(OW, OH, F, st.frame, uid);
      svg.appendChild(fr.under); svg.appendChild(board); svg.appendChild(fr.over);
    } else {
      svg.appendChild(board);
    }
    svg.__mym = { key: st.size + '|' + st.frame, W: D.W, H: H, uid: uid };
    lift(svg, st, uid);
    return svg;
  }

  /* Redraw the type and ornaments of an existing drawing (same size and frame), keeping its basemap. */
  function refresh(svg, st, opts) {
    var board = svg.querySelector('[data-mym-board]'), ink = inksFor(st.style);
    var inkG = board.querySelector('[data-mym-ink]'), uid = 'kmym' + (++uidCounter) + '-';
    setKids(inkG, ornaments(st, opts, uid));
    lift(svg, st, uid);
    board.querySelector('[data-mym-paper]').setAttribute('fill', ink.paper);
    svg.setAttribute('data-mym-style', st.style);
    if (opts.label) svg.setAttribute('aria-label', opts.label);
  }

  /* Needs the drawing in the document: measures the type in the real fonts, shrinks a line that is too
     wide (to 60 % at most), lays the legend out in equal columns and spans the rule to suit it. */
  function layout(svg) {
    var style = svg.getAttribute('data-mym-style') || 'neutral';
    var gm = geo(style), TYPE = gm.TYPE, LEGEND = gm.LEGEND, RULE = gm.RULE;
    var texts = svg.querySelectorAll('text[data-fit]');
    for (var i = 0; i < texts.length; i++) {
      var t = texts[i], size = +t.getAttribute('data-size'), track = +t.getAttribute('data-track'), max = +t.getAttribute('data-fit');
      setType(t, size, track);
      var w = inkWidth(t, size * track);
      if (w > max) setType(t, size * Math.max(FIT_MIN, max / w), track);
    }
    if (kindOf(style) === 'april') { layoutApril(svg, aprilGeo(style)); return; }
    if (kindOf(style) === 'badge') { layoutBadge(svg); return; }  // every line has its place, fitted above; short legends centre
    var g = svg.querySelector('[data-mym-legend]'), rowW = 0;
    if (g) {
      var items = g.querySelectorAll('[data-item]'), n = items.length, ls = TYPE.legend.size * TYPE.legend.track;
      var widths = [], starts = [], gaps = [];
      for (var j = 0; j < n; j++) {
        /* a style can set its own ring-to-words gap per item (Navy's second item sits further out) */
        gaps.push(LEGEND.gapTexts && LEGEND.gapTexts[j] != null ? +LEGEND.gapTexts[j] : LEGEND.gapText);
        widths.push(LEGEND.ring + gaps[j] + inkWidth(items[j].lastChild, ls));
      }
      var cy = +g.getAttribute('data-cy'), cxl = LEGEND.cx;
      if (n === 1) starts = [cxl - widths[0] / 2];
      else if (n === 2) starts = [cxl - LEGEND.hugL - widths[0], cxl + LEGEND.hugR];
      else starts = [cxl - widths[1] / 2 - LEGEND.gap3 - widths[0], cxl - widths[1] / 2, cxl + widths[1] / 2 + LEGEND.gap3];
      /* the divider and the compass stay on the centre line however long either side runs */
      for (var m = 0; m < n; m++) {
        items[m].firstChild.setAttribute('cx', (starts[m] + LEGEND.ring / 2).toFixed(1));
        items[m].lastChild.setAttribute('x', (starts[m] + LEGEND.ring + gaps[m]).toFixed(1));
      }
      /* the row's reach either side of the centre line decides whether it fits and how far the rule runs */
      var reach = Math.max(cxl - starts[0], starts[n - 1] + widths[n - 1] - cxl);
      rowW = 2 * reach;
      var k = rowW > LEGEND.fit ? Math.max(LEGEND.min, LEGEND.fit / rowW) : 1;
      if (k < 1) g.setAttribute('transform', 'translate(' + LEGEND.cx + ' ' + cy + ') scale(' + k.toFixed(4) + ') translate(' + (-LEGEND.cx) + ' ' + (-cy) + ')');
      else g.removeAttribute('transform');
      rowW *= k;
    }
    var rule = svg.querySelector('[data-mym-rule]');
    if (rule) {
      var half = Math.min(RULE.max, Math.max(RULE.half, rowW * RULE.share / 2));
      var l = rule.querySelector('[data-seg="l"]'), r = rule.querySelector('[data-seg="r"]');
      l.setAttribute('x', (RULE.cx - half).toFixed(1)); l.setAttribute('width', (half - RULE.gap).toFixed(1));
      r.setAttribute('x', (RULE.cx + RULE.gap).toFixed(1)); r.setAttribute('width', (half - RULE.gap).toFixed(1));
    }
  }
  function setType(t, size, track) {
    t.setAttribute('font-size', size.toFixed(1));
    t.setAttribute('letter-spacing', (size * track).toFixed(1));
    /* a badge line sits centred in its place at full size (on the ribbon, by its ring, in the frame), so one that
       shrinks to fit rises by half what its capitals lose, and stays centred: long names don't sink on the ribbon */
    if (t.hasAttribute('data-y')) t.setAttribute('y', (+t.getAttribute('data-y') - (+t.getAttribute('data-size') - size) * BADGE.cap / 2).toFixed(1));
    if (t.getAttribute('text-anchor') === 'start') return;      // a line set from its left edge keeps it
    var cx = t.hasAttribute('data-cx') ? +t.getAttribute('data-cx') : CX;      // centred off the inner rule (the badge)
    t.setAttribute('x', (cx + (+t.getAttribute('data-dx') || 0) + size * track / 2).toFixed(1));
  }
  function inkWidth(t, ls) {
    var w = 0;
    try { w = t.getComputedTextLength(); } catch (e) {}
    return Math.max(0, w - ((t.textContent || '').length ? ls : 0));
  }

  /* ── zoom: a viewBox around the band being typed in: the Desk Size and April maps' bands at least 4200 px across;
        the Muted Tones badge closer, 250 px of map around it (Kevin, 17 Oct 2026: "need it to zoom in further") ── */
  var ZOOM = { pad: 420, w: 4200 }, ZOOM_BADGE = { pad: 250, w: 1500 };
  function bandBox(svg, band) {
    var board = svg.querySelector('[data-mym-board]'); if (!board) return null;
    var BW = svg.__mym.W || MW, H = svg.__mym.H, nodes = board.querySelectorAll('[data-mym-ink] > *'), box = null;
    for (var i = 0; i < nodes.length; i++) {
      var b; try { b = nodes[i].getBBox(); } catch (e) { continue; }
      if (!b.width) continue;
      var mid = b.y + b.height / 2;
      if (band === 'top' ? mid > H * 0.3 : mid < H * 0.6) continue;
      box = box ? { x0: Math.min(box.x0, b.x), y0: Math.min(box.y0, b.y), x1: Math.max(box.x1, b.x + b.width), y1: Math.max(box.y1, b.y + b.height) }
                : { x0: b.x, y0: b.y, x1: b.x + b.width, y1: b.y + b.height };
    }
    if (!box) return null;
    var Z = kindOf(svg.getAttribute('data-mym-style') || '') === 'badge' ? ZOOM_BADGE : ZOOM;
    var pad = Z.pad, w = Math.max(Z.w, box.x1 - box.x0 + 2 * pad), h = w * H / BW;
    if (h < box.y1 - box.y0 + 2 * pad) { h = box.y1 - box.y0 + 2 * pad; w = h * BW / H; }
    w = Math.min(BW, w); h = Math.min(H, h);
    var x = Math.max(0, Math.min(BW - w, (box.x0 + box.x1) / 2 - w / 2)), y = Math.max(0, Math.min(H - h, (box.y0 + box.y1) / 2 - h / 2));
    return [x, y, w, h];
  }
  function animateView(board, to) {
    var from = (board.getAttribute('viewBox') || '').split(/\s+/).map(Number);
    if (board.__anim) cancelAnimationFrame(board.__anim);
    if (from.length !== 4 || reduceMotion) { board.setAttribute('viewBox', to.map(function (v) { return v.toFixed(1); }).join(' ')); return; }
    var t0 = performance.now(), dur = 300;
    (function step(now) {
      var k = Math.min(1, (now - t0) / dur), e = k < 0.5 ? 2 * k * k : 1 - Math.pow(-2 * k + 2, 2) / 2;
      board.setAttribute('viewBox', from.map(function (v, i) { return (v + (to[i] - v) * e).toFixed(1); }).join(' '));
      board.__anim = k < 1 ? requestAnimationFrame(step) : 0;
    })(t0);
  }

  /* ───────────────────────────── the product-page controller ───────────────────────────── */

  function mount(root) {
    if (root.__mym) return root.__mym;
    var cfg = {};
    try { cfg = JSON.parse(root.querySelector('script[data-mym-config]').textContent); } catch (e) {}
    var $ = function (sel) { return root.querySelector(sel); };
    var $$ = function (sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); };
    var scope = root.closest('product-info') || document;
    var noneInput = $('[data-mym-none]');
    var draftKey = 'kr-mym:' + (cfg.handle || location.pathname);
    /* the photo gallery's own "Your map" slide and thumbnail (snippets/kr-make-your-map-media.liquid) */
    var slide = scope.querySelector('[data-mym-gallery]');
    var gallery = slide && slide.closest('media-gallery');
    var thumb = gallery && gallery.querySelector('.kr-mym-thumb');
    var thumbImg = thumb && thumb.querySelector('[data-mym-thumb]');
    var wide = window.matchMedia('(min-width: 750px)');      // Dawn's two-column, one-photo-at-a-time gallery
    if (slide) root.classList.add('has-gallery');

    /* the Muted Tones badge: up to five legend lines (the layout follows how many are filled), or a quote */
    var badgeMap = cfg.layout === 'badge';
    var state = {
      title: '', subtitle: '', legendCount: badgeMap ? 5 : 2, legend: badgeMap ? ['', '', '', '', ''] : ['', '', ''], names: '', dateline: '', notes: '', script: '',
      quoteMode: false, quote1: '', quote2: '', quoteBy: '', lines: 2,
      include: { title: true, subtitle: true, legend: true, names: true, dateline: true, quote: true },
      skipped: false, noteOpen: false,
      style: cfg.style || 'neutral', styleName: '', size: 's20', frame: 'none'
    };
    var limits = cfg.layout === 'april' ? LIMITS_APRIL : badgeMap ? LIMITS_BADGE : LIMITS;
    var field = {
      title: $('[data-mym="title"]'), subtitle: $('[data-mym="subtitle"]'), names: $('[data-mym="names"]'),
      dateline: $('[data-mym="dateline"]'), notes: $('[data-mym="notes"]'),
      quote1: $('[data-mym="quote1"]'), quote2: $('[data-mym="quote2"]'), quoteBy: $('[data-mym="quoteBy"]'),
      legend: [1, 2, 3, 4, 5].map(function (n) { return $('[data-mym="legend-' + n + '"]'); })
    };

    /* ── the variant picker decides style, size and frame ── */
    var optIndex = { style: -1, size: -1, frame: -1 }, skipPos = -1, materialPos = -1;
    (cfg.options || []).forEach(function (name, i) {
      var n = String(name).toLowerCase();
      /* the Muted Tones listings: Legend (No Legend / Personalized) on the USA map, Personalized (No Personalization /
         Personalized) on the world map, is the skip; Style is the material */
      if (badgeMap) {
        if (/legend|personali[sz]/.test(n)) skipPos = i; else if (/size/.test(n)) optIndex.size = i; else if (/style|material/.test(n)) materialPos = i;
        return;
      }
      if (/frame/.test(n)) optIndex.frame = i;
      else if (/size/.test(n)) optIndex.size = i;
      else if (/style|color|colour/.test(n)) optIndex.style = i;
    });
    /* some listings fold the frame into the size ("11x14" + Frame" / "11x14" No Frame"): white oak or none */
    var frameInSize = optIndex.frame < 0 && optIndex.size > -1 && /frame/i.test(String((cfg.variant || [])[optIndex.size] || ''));
    var framePos = optIndex.frame > -1 ? optIndex.frame + 1 : frameInSize ? optIndex.size + 1 : 0;
    function applyOptions(values) {
      if (!values) return false;
      var before = [state.style, state.size, state.frame, state.skipped].join('|');
      if (optIndex.style > -1 && values[optIndex.style] != null) { state.style = styleKey(values[optIndex.style]); state.styleName = String(values[optIndex.style]); }
      if (optIndex.size > -1 && values[optIndex.size] != null) state.size = sizeKey(values[optIndex.size]);
      if (optIndex.frame > -1 && values[optIndex.frame] != null) state.frame = frameKey(values[optIndex.frame]);
      if (frameInSize && values[optIndex.size] != null) state.frame = /no\s*frame/i.test(values[optIndex.size]) ? 'none' : 'white';
      if (materialPos > -1 && values[materialPos] != null) state.styleName = String(values[materialPos]);
      /* where an option is the skip (No Legend), it decides: the words wait in the fields */
      if (skipPos > -1 && values[skipPos] != null) skipTo(/^no\s/i.test(String(values[skipPos])));
      return before !== [state.style, state.size, state.frame, state.skipped].join('|');
    }
    function skipTo(on) { state.skipped = !!on; PARTS.forEach(function (k) { state.include[k] = !on; }); }
    var focusNext = null;                                // a field to focus once the variant change has landed
    /* pick the skip option's value in the variant picker, as a click would (true: the personalized one) */
    function pickSkip(personal) {
      if (skipPos < 0) return false;
      var inp = Array.prototype.filter.call(scope.querySelectorAll('variant-selects input[type="radio"][data-opt-pos="' + (skipPos + 1) + '"]'),
        function (i) { return /^no\s/i.test(i.value) !== personal; })[0];
      if (!inp) return false;
      if (!inp.checked) { var lab = scope.querySelector('label[for="' + inp.id + '"]'); (lab || inp).click(); }
      return true;
    }
    function readPicker() {
      var values = (cfg.variant || []).slice();
      scope.querySelectorAll('variant-selects input[type="radio"]:checked, variant-selects select').forEach(function (inp) {
        var pos = +(inp.getAttribute('data-opt-pos') || 0);
        if (!pos) { var m = /-(\d+)$/.exec(inp.name || ''); if (m) pos = +m[1]; }
        if (!pos && inp.tagName === 'SELECT') { var mm = /-(\d+)$/.exec(inp.id || ''); if (mm) pos = +mm[1] + 1; }
        if (pos) values[pos - 1] = inp.value;
      });
      return values;
    }
    applyOptions(readPicker());
    /* picking a style, size or frame shows it on their map, in the photo spot */
    scope.addEventListener('change', function (e) {
      if (!(e.target.closest && e.target.closest('variant-selects'))) return;
      if (applyOptions(readPicker())) { syncForm(); preload(); render(); }
      showMap();
    });
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, function (ev) {
        var v = ev && ev.data && ev.data.variant;
        if (v && v.options && applyOptions(v.options)) { syncForm(); preload(); render(); }
        /* Add words instead picked the personalized option: once Dawn has put focus back on that option, the first field */
        if (focusNext) { var fn = focusNext; focusNext = null; setTimeout(function () { if (!state.skipped && !fn.disabled) fn.focus(); }, 0); }
        /* Dawn may have brought a variant photo forward and to the front: put the map back */
        keepPlace();
        showMap();
      });
    }

    /* ── the draft: their words survive a reload or a look at another page ── */
    try {
      var saved = JSON.parse(localStorage.getItem(draftKey) || 'null');
      if (saved && typeof saved === 'object') {
        TEXTS.forEach(function (k) { if (typeof saved[k] === 'string') state[k] = saved[k]; });
        if (Array.isArray(saved.legend)) state.legend = state.legend.map(function (x, i) { return typeof saved.legend[i] === 'string' ? saved.legend[i] : ''; });
        if (saved.legendCount === 3 && !badgeMap) state.legendCount = 3;
        if (typeof saved.script === 'string') state.script = saved.script;
        if (saved.quoteMode === true) state.quoteMode = true;
        if (saved.lines >= 2 && saved.lines <= 5) state.lines = saved.lines;
        if (saved.skipped === true) { state.skipped = true; PARTS.forEach(function (k) { state.include[k] = false; }); }
      }
    } catch (e) {}
    /* where an option is the skip, the option picked wins over the draft */
    if (skipPos > -1) skipTo(/^no\s/i.test(String(readPicker()[skipPos] || '')));
    function saveDraft() {
      try {
        localStorage.setItem(draftKey, JSON.stringify({ title: state.title, subtitle: state.subtitle, legend: state.legend, legendCount: state.legendCount,
          names: state.names, dateline: state.dateline, notes: state.notes, script: state.script, skipped: state.skipped,
          quoteMode: state.quoteMode, quote1: state.quote1, quote2: state.quote2, quoteBy: state.quoteBy, lines: state.lines }));
      } catch (e) {}
    }

    /* ── form <-> state ── */
    function syncForm() {
      if (!state.include.title) state.include.subtitle = false;          // a subtitle never prints without a title
      TEXTS.forEach(function (k) { if (field[k] && field[k].value !== state[k]) field[k].value = state[k]; });
      field.legend.forEach(function (inp, i) { if (inp && inp.value !== state.legend[i]) inp.value = state.legend[i]; });
      $$('[data-mym-count]').forEach(function (b) { b.setAttribute('aria-pressed', String(+b.getAttribute('data-mym-count') === state.legendCount)); });
      var third = $('[data-mym-third]'); if (third) third.hidden = state.legendCount !== 3;
      PARTS.forEach(function (k) {
        var on = state.include[k], box = $('[data-part="' + k + '"]');
        var body = box && box.querySelector('[data-mym-body]');
        if (body) body.hidden = !on;
        if (box) box.classList.toggle('is-off', !on);
      });
      /* Skip personalization switches every part off and folds the fields away; their words wait in them */
      var fields = $('[data-mym-fields]'), skippedBox = $('[data-mym-skipped]'), skipLine = $('[data-mym-skipline]');
      if (fields) fields.hidden = !!state.skipped;
      if (skippedBox) skippedBox.hidden = !state.skipped;
      if (skipLine) skipLine.hidden = !!state.skipped;
      var orphan = $('[data-mym-orphan]');
      if (orphan) orphan.hidden = !!trim(state.title) || !trim(state.subtitle);
      /* a part that is switched off is never submitted */
      off(field.title, !state.include.title);
      off(field.subtitle, !state.include.subtitle);
      off(field.names, !state.include.names);
      off(field.dateline, !state.include.dateline);
      field.legend.forEach(function (inp, i) { off(inp, !state.include.legend || i >= state.legendCount); });
      if (noneInput) noneInput.disabled = !isBlank(shown(state));
      /* the counter shows from three quarters of the limit; past it the line prints smaller, and a note says so */
      $$('[data-mym-counter]').forEach(function (c) {
        var key = c.getAttribute('data-mym-counter'), inp = $('[data-mym="' + key + '"]'); if (!inp) return;
        var lim = limits[key.replace(/-\d$/, '')] || 30, n = inp.value.length;
        c.textContent = n + ' / ' + lim;
        c.classList.toggle('is-over', n > lim);
        c.hidden = n < Math.ceil(lim * 0.75);
      });
      $$('[data-mym-over]').forEach(function (h) {
        var key = h.getAttribute('data-mym-over');
        h.hidden = !(key === 'legend'
          ? state.legend.slice(0, state.legendCount).some(function (t) { return t.length > limits.legend; })
          : key === 'quote' ? ['quote1', 'quote2', 'quoteBy'].some(function (k) { return state[k].length > limits[k]; })
          : String(state[key] || '').length > (limits[key] || 99));
      });
      /* April: the title's words as buttons; one tap sets that word in script, None sets none. The buttons are
         rebuilt only when the words change, so a tap keeps its focus. */
      var pick = $('[data-mym-script-words]'), scriptInput = $('[data-mym-script]');
      if (pick) {
        var words = trim(state.title) ? trim(state.title).split(' ') : [], sk = scriptIndex(words, state.script);
        if (state.script && sk < 0) state.script = '';                 // the word left the title
        var sig = words.join('\u0001');
        if (pick.__sig !== sig) {
          pick.__sig = sig;
          setKids(pick, words.map(function (w) { return wordButton(w); }).concat([wordButton('')]));
        }
        Array.prototype.forEach.call(pick.children, function (b, i) { b.setAttribute('aria-pressed', String(i === words.length ? sk < 0 : i === sk)); });
        var pickBox = $('[data-mym-script-pick]'); if (pickBox) pickBox.hidden = !words.length;
        /* the order gets the word as the map shows it */
        if (scriptInput) { scriptInput.value = sk >= 0 ? scriptCase(words[sk]) : ''; scriptInput.disabled = sk < 0 || !state.include.title; }
      }
      /* Muted Tones: names, a date and up to five legend lines, or a quote. The legend shows two lines and one more
         per "Add a line"; whatever isn't in use isn't sent; the order names the PSD layout it is. */
      if (badgeMap) {
        var q = !!state.quoteMode, lines = Math.max(state.lines, filledLines());
        /* the cards: names & legend or a quote, or Just the map while the listing's No Legend is picked */
        $$('[data-mym-kind]').forEach(function (b) {
          var k = b.getAttribute('data-mym-kind');
          b.setAttribute('aria-pressed', String(state.skipped ? k === 'none' : k === (q ? 'quote' : 'names')));
        });
        $$('[data-mym-names-side]').forEach(function (n) { n.hidden = q; });
        $$('[data-mym-quote-side]').forEach(function (n) { n.hidden = !q; });
        $$('[data-mym-line]').forEach(function (row) { row.hidden = +row.getAttribute('data-mym-line') > lines; });
        var add = $('[data-mym-addline]'); if (add) add.hidden = lines >= 5;
        off(field.names, q || !state.include.names);
        off(field.dateline, q || !state.include.dateline);
        field.legend.forEach(function (inp, i) { off(inp, q || !state.include.legend || i >= lines); });
        ['quote1', 'quote2', 'quoteBy'].forEach(function (k) { off(field[k], !q || !state.include.quote); });
        var layoutInput = $('[data-mym-layout]'), lk = badgeLayout(state);
        if (layoutInput) { layoutInput.value = lk ? BADGE_NAMES[lk] : ''; layoutInput.disabled = !lk; }
      }
      /* the suggestion already in the field shows as picked */
      $$('[data-mym-fill]').forEach(function (b) {
        var key = b.getAttribute('data-mym-fill'), val = b.getAttribute('data-value') || b.textContent;
        var on = key === 'legend'
          ? (badgeMap ? val === state.legend.map(trim).filter(Boolean).join('|')
                      : val.split('|').length === state.legendCount && val === state.legend.slice(0, state.legendCount).join('|'))
          : key === 'quote' ? val === [state.quote1, state.quote2, state.quoteBy].join('|')
          : state[key] === val;
        b.setAttribute('aria-pressed', String(on));
      });
      /* the note opens when asked for, or by itself when it already holds words */
      var noteBody = $('[data-mym-note-body]'), noteLink = $('[data-mym-note-link]'), noteOpen = state.noteOpen || !!trim(state.notes);
      if (noteBody) noteBody.hidden = !noteOpen;
      if (noteLink) noteLink.hidden = noteOpen;
    }
    function off(inp, yes) { if (inp) inp.disabled = !!yes; }
    function filledLines() { var n = 0; state.legend.forEach(function (t, i) { if (trim(t)) n = i + 1; }); return n; }
    function wordButton(w) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'kr-mym__chip kr-mym__word' + (w ? '' : ' kr-mym__word--none');
      b.setAttribute('data-mym-script-word', w);
      b.textContent = w ? scriptCase(w) : 'None';
      return b;
    }
    function readForm() {
      TEXTS.forEach(function (k) { if (field[k]) state[k] = field[k].value; });
      field.legend.forEach(function (inp, i) { if (inp) state.legend[i] = inp.value; });
    }
    function update() { syncForm(); showMap(); render(); saveDraft(); }

    root.addEventListener('input', function (e) {
      if (!e.target.matches('[data-mym]')) return;
      readForm(); update();
      /* typing into a part that had nothing on the map yet: zoom in once its words appear */
      var part = e.target.closest('[data-part]'), name = part && part.getAttribute('data-part');
      if (PARTS.indexOf(name) > -1 && zoomed !== bandOf(name) && document.activeElement === e.target) zoomTo(bandOf(name));
    });
    root.addEventListener('click', function (e) {
      var t = e.target;
      var word = t.closest('[data-mym-script-word]');
      if (word) { state.script = word.getAttribute('data-mym-script-word'); update(); return; }
      var chip = t.closest('[data-mym-fill]');
      if (chip) {
        var key = chip.getAttribute('data-mym-fill'), val = chip.getAttribute('data-value') || chip.textContent;
        if (key === 'legend') {
          var parts = val.split('|');
          if (badgeMap) {
            state.legend = state.legend.map(function (x, i) { return parts[i] || ''; });
            state.lines = Math.max(2, parts.length);
          } else {
            state.legendCount = parts.length === 3 ? 3 : 2;
            state.legend = [parts[0] || '', parts[1] || '', parts[2] || state.legend[2]];
          }
          state.include.legend = true;
        } else if (key === 'quote') {
          var qp = val.split('|');
          state.quote1 = qp[0] || ''; state.quote2 = qp[1] || ''; state.quoteBy = qp[2] || '';
          state.quoteMode = true; state.include.quote = true;
        } else {
          state[key] = val;
          state.include[key] = true;
          if (key === 'subtitle') state.include.title = true;
          if (key === 'title' && chip.hasAttribute('data-script')) state.script = chip.getAttribute('data-script');   // April
        }
        update(); return;
      }
      var count = t.closest('[data-mym-count]');
      if (count) { state.legendCount = +count.getAttribute('data-mym-count') === 3 ? 3 : 2; update(); return; }
      /* where an option is the skip (No Legend), Skip picks it and the variant change folds the fields */
      if (t.closest('[data-mym-skip]')) { if (!pickSkip(false)) skipTo(true); update(); return; }
      if (t.closest('[data-mym-unskip]')) {
        var first = field.title || field.names;
        if (pickSkip(true)) focusNext = first; else skipTo(false);
        update();
        if (first) first.focus();
        return;
      }
      /* Muted Tones: a card picks names & legend or a quote, and brings the words back from Just the map by
         picking the personalized option; Just the map picks No Legend, as Skip does on the other maps */
      var kind = t.closest('[data-mym-kind]');
      if (kind) {
        var k = kind.getAttribute('data-mym-kind');
        if (k === 'none') { if (!pickSkip(false)) skipTo(true); update(); return; }
        state.quoteMode = k === 'quote';
        var lead = state.quoteMode ? field.quote1 : field.names;
        if (state.skipped) { if (pickSkip(true)) focusNext = lead; else skipTo(false); }
        update();
        if (lead) lead.focus();
        return;
      }
      if (t.closest('[data-mym-addline]')) {
        state.lines = Math.min(5, Math.max(state.lines, filledLines()) + 1);
        update();
        var next = field.legend[state.lines - 1]; if (next) next.focus();
        return;
      }
      if (t.closest('[data-mym-note-open]')) { state.noteOpen = true; syncForm(); if (field.notes) field.notes.focus(); return; }
      if (t.closest('[data-mym-reset]')) {
        state.title = state.subtitle = state.names = state.dateline = state.notes = state.quote1 = state.quote2 = state.quoteBy = '';
        state.legend = state.legend.map(function () { return ''; }); state.legendCount = badgeMap ? 5 : 2; state.lines = 2;
        state.quoteMode = false; state.skipped = false;
        PARTS.forEach(function (k) { state.include[k] = true; });
        if (skipPos > -1) skipTo(/^no\s/i.test(String(readPicker()[skipPos] || '')));      // the option still decides
        update(); return;
      }
      stageClick(e);
    });
    /* on the map itself (either copy): a zoomed map backs out, otherwise it opens larger, as a photo would */
    function stageClick(e) {
      var t = e.target;
      if (t.closest('[data-mym-closeup]')) { openProof(true); return; }
      if (t.closest('[data-mym-open]')) { openProof(); return; }
      if (!t.closest('[data-mym-stage]')) return;
      if (zoomed) zoomOut(true); else openProof();
    }
    if (slide) slide.addEventListener('click', stageClick);

    /* ── the sign-off. No proof is emailed: the buyer approves the preview before anything can be bought,
          and a change to what prints (words, parts, style, size) takes the approval away again. The frame
          and the order notes don't print, so changing them keeps it. Or the buyer says it's not quite right
          and writes what to adjust: that goes with the order as Special request, without the approval, and
          the shop makes the change by hand and emails a proof. ── */
    var approve = $('[data-mym-approve]'), approveMsg = $('[data-mym-approve-msg]'), approvedInput = $('[data-mym-approved]');
    var request = $('[data-mym-request]'), requestBody = $('[data-mym-request-body]'), requestText = $('[data-mym-request-text]');
    var signoff = $('[data-mym-signoff]'), approvedKey = null;
    /* the drawing's height, not the size: the 24 x 36 maps' two sizes are one drawing, so either keeps the approval */
    function designKey() { return JSON.stringify([shown(state), state.legendCount, state.style, boardOf(state).H]); }
    function isApproved() { return !!approve && approve.checked && approvedKey === designKey(); }
    function isRequested() { return !!request && request.checked; }
    function requestReady() { return isRequested() && !!requestText && !!trim(requestText.value); }
    function signedOff() { return isApproved() || requestReady(); }
    /* where an option is the skip (the Muted Tones maps' Personalized costs more than No Legend / No Personalization),
       its personalized pick with nothing typed would print the plain map at the higher price: it can't be approved
       like that. cfg.skipName: that option's own "No …" choice, as the listing names it */
    var EMPTY_SAY = 'There’s nothing in your legend yet. Add your words above, or choose ' + (cfg.skipName || 'No Legend') + '.';
    function emptyPersonal() { return skipPos > -1 && !state.skipped && isBlank(shown(state)); }
    function say(text) {
      if (!approveMsg) return;
      approveMsg.textContent = text || '';
      approveMsg.hidden = !text;
    }
    function syncSignoff() {
      var asking = isRequested();
      if (requestBody) requestBody.hidden = !asking;
      if (requestText) requestText.disabled = !asking;               // only an asked-for change reaches the order
      if (signoff) signoff.classList.toggle('is-request', asking);
      holdExpress();
    }
    function setApproved(on) {
      approvedKey = on ? designKey() : null;
      if (approve) approve.checked = !!on;
      if (approvedInput) approvedInput.disabled = !on;
      if (signoff) { signoff.classList.toggle('is-approved', !!on); if (on) signoff.classList.remove('is-asking'); }
      syncSignoff();
    }
    function recheck() {
      if (approvedKey && approvedKey !== designKey()) {
        setApproved(false);
        say('Your map changed. Approve it again, or tell us what to adjust.');
      }
    }
    if (approve) approve.addEventListener('change', function () {
      if (approve.checked && emptyPersonal()) { setApproved(false); say(EMPTY_SAY); return; }
      setApproved(approve.checked); say('');
    });
    if (request) request.addEventListener('change', function () {
      if (!request.checked) return;
      setApproved(false); say('');
      if (requestText) requestText.focus();
    });
    if (requestText) requestText.addEventListener('input', function () {
      syncSignoff();
      if (requestReady()) { say(''); if (signoff) signoff.classList.remove('is-asking'); }
    });
    function stop(e) {
      e.preventDefault();
      e.stopPropagation();
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();
    }
    /* not signed off: stop the purchase, say why, and bring the choice into view; signed off with no frame
       picked, check that once */
    function gate(e) {
      if (!approve) return false;
      if (signedOff()) return frameGate(e);
      stop(e);
      var asking = isRequested();
      say(asking ? 'Tell us what to adjust first.' : emptyPersonal() ? EMPTY_SAY : 'Approve your design first, or tell us what needs adjusting.');
      if (signoff) {
        signoff.classList.add('is-asking');
        signoff.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
      }
      var target = asking && requestText ? requestText : approve;
      setTimeout(function () { target.focus({ preventScroll: true }); }, reduceMotion ? 0 : 400);
      return true;
    }

    /* ── no frame by default: the listing opens on No frame, so Add to cart asks once whether they really
          want it unframed. Picking any frame option themselves, No frame included, means they chose. ── */
    var frameCheck = $('[data-mym-framecheck]'), frameChosen = false, frameKept = false, frameAgain = null, frameVia = '', frameFrom = null;
    /* on the page itself, as the larger view is: the product column can trap a fixed overlay */
    if (frameCheck) document.body.appendChild(frameCheck);
    function frameNeedsCheck() { return !!frameCheck && !!framePos && state.frame === 'none' && !frameChosen && !frameKept; }   // only where a frame can be picked
    /* express buttons are iframes: while anything still needs saying, their clicks fall through to the page */
    function holdExpress() { document.documentElement.classList.toggle('kr-mym-unapproved', !signedOff() || frameNeedsCheck()); }
    function frameGate(e) {
      if (!frameNeedsCheck()) return false;
      stop(e);
      frameVia = e.type;
      frameAgain = e.type === 'submit' ? (e.submitter || null) : null;
      openFrameCheck();
      return true;
    }
    function openFrameCheck() {
      frameFrom = document.activeElement;
      frameCheck.hidden = false;
      var first = frameCheck.querySelector('[data-mym-frame-choose]');
      if (first) first.focus();
    }
    function closeFrameCheck() {
      frameCheck.hidden = true;
      if (frameFrom && frameFrom.focus && document.contains(frameFrom)) frameFrom.focus({ preventScroll: true });
    }
    function frameGroup() {
      var inp = framePos && scope.querySelector('variant-selects input[type="radio"][data-opt-pos="' + framePos + '"]');
      return inp ? inp.closest('fieldset') || inp.parentElement : null;
    }
    /* where the size carries the frame, the framed version of the same size */
    function framedPick(g) {
      if (!frameInSize) return g.querySelector('input[type="radio"]:not(:checked)');
      var radios = g.querySelectorAll('input[type="radio"]'), cur = g.querySelector('input[type="radio"]:checked');
      var dims = cur ? String(cur.value).split(/\s/)[0] : '';
      for (var i = 0; i < radios.length; i++) {
        if (String(radios[i].value).split(/\s/)[0] === dims && !/no\s*frame/i.test(radios[i].value)) return radios[i];
      }
      return null;
    }
    if (frameCheck) {
      frameCheck.addEventListener('click', function (e) {
        if (e.target === frameCheck) { closeFrameCheck(); return; }        // the backdrop: just close
        if (e.target.closest('[data-mym-frame-choose]')) {
          closeFrameCheck();
          var g = frameGroup();
          if (g) {
            g.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
            var pickable = framedPick(g);
            if (pickable) setTimeout(function () { pickable.focus({ preventScroll: true }); }, reduceMotion ? 0 : 400);
          }
          return;
        }
        if (e.target.closest('[data-mym-frame-keep]')) {
          frameKept = true; holdExpress(); closeFrameCheck();
          /* Add to cart carries on by itself; an express button (an iframe) needs its own second tap */
          var form = document.getElementById(cfg.formId), btn = frameAgain || (form && form.querySelector('[type="submit"]'));
          var resubmit = frameVia === 'submit';
          frameAgain = null; frameVia = '';
          if (resubmit && btn) btn.click();
        }
      });
      frameCheck.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') { e.preventDefault(); closeFrameCheck(); return; }
        if (e.key !== 'Tab') return;
        var b = frameCheck.querySelectorAll('button'), first = b[0], last = b[b.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      });
    }
    /* their own click on any Frame style option (or Size, where the size carries the frame), the one already
       picked included */
    scope.addEventListener('click', function (e) {
      var t = e.target;
      if (!t || !t.matches || !t.matches('variant-selects input[type="radio"]') || !framePos) return;
      if (+(t.getAttribute('data-opt-pos') || 0) === framePos) { frameChosen = true; holdExpress(); }
    });
    setApproved(false);
    /* Shop Pay and "More payment options" go to checkout without submitting the form: catch their clicks
       on the way down (they live in shadow roots and iframes; the CSS lets iframe clicks fall through) */
    document.addEventListener('click', function (e) {
      var path = e.composedPath ? e.composedPath() : [e.target];
      for (var i = 0; i < path.length; i++) {
        var n = path[i];
        if (n && n.classList && n.classList.contains('shopify-payment-button')) { if (scope.contains(n)) gate(e); return; }
      }
    }, true);

    /* ── hold back blank properties at submit (the capture phase runs before the theme's own handler).
          The product form holds an input named "id", which shadows form.id, so read the attribute. ── */
    document.addEventListener('submit', function (e) {
      if (!e.target || !e.target.getAttribute || e.target.getAttribute('id') !== cfg.formId) return;
      if (gate(e)) return;
      var s = shown(state), held = [];
      $$('[data-mym-prop]').forEach(function (inp) {
        var unprinted = inp === field.subtitle && !s.subtitle;          // a subtitle without a title doesn't print, so isn't sent
        if (!inp.disabled && (!trim(inp.value) || unprinted)) { inp.disabled = true; held.push(inp); }
      });
      if (held.length) setTimeout(function () { held.forEach(function (inp) { inp.disabled = false; }); syncForm(); }, 0);
    }, true);

    /* ── basemaps: fetch the next one before showing it, so the map never blinks out ── */
    var ready = {};
    function load(url, then) {
      if (!url) return;
      if (ready[url] === true) { if (then) then(); return; }
      if (ready[url]) { ready[url].push(then); return; }
      ready[url] = [then];
      var img = new Image();
      img.onload = img.onerror = function () { var cbs = ready[url]; ready[url] = true; cbs.forEach(function (f) { if (f) f(); }); };
      img.src = url;
    }
    function preload(large) {
      var b = (cfg.bases || {})[state.style] || {};
      (large ? [b.bandLg || b.band, b.fullLg || b.full] : [b.band, b.full]).forEach(function (u) { load(u); });
    }
    function showBase(view, url) {
      var img = view.svg.querySelector('[data-mym-base]');
      if (!img || img.getAttribute('href') === url) return;
      /* a new style shows its own paper until its map arrives, never the last style's map under its inks */
      if (ready[url] !== true && view.style && view.style !== state.style) img.setAttribute('href', '');
      view.style = state.style;
      load(url, function () { if (img.isConnected) img.setAttribute('href', url); });
    }
    /* hovering or focusing a Map Style card starts its maps loading, so a click usually finds them ready */
    function warm(e) {
      var lab = e.target.closest && e.target.closest('variant-selects label[for]');
      var inp = lab && document.getElementById(lab.getAttribute('for'));
      if (!inp || optIndex.style < 0 || +(inp.getAttribute('data-opt-pos') || 0) !== optIndex.style + 1) return;
      var b = (cfg.bases || {})[styleKey(inp.value)] || {};
      load(b.band); load(b.full);
    }
    scope.addEventListener('pointerover', warm);
    scope.addEventListener('focusin', warm);

    /* ── drawing: one drawing per place the map is shown (the gallery slide, the panel). A place that is
          hidden right now (a photo is showing, or the panel's copy on a wide screen) can't be measured,
          so it is drawn when it next appears. ── */
    var zoomed = null;
    var views = [];
    function addView(stageEl) { if (stageEl) views.push({ stage: stageEl, svg: null, dirty: true, style: '' }); }
    if (slide) addView(slide.querySelector('[data-mym-stage]'));
    addView($('[data-mym-stage]'));
    function shownNow(el) { return !!(el && el.getClientRects().length); }
    function ariaLabel() {
      var s = shown(state), bits = [];
      if (s.title) bits.push('title ' + s.title);
      if (s.subtitle) bits.push('subtitle ' + s.subtitle);
      if (s.items.length) bits.push('legend ' + s.items.join(', '));
      if (s.names) bits.push('names ' + s.names);
      if (s.dateline) bits.push(s.dateline);
      if (s.quote.some(Boolean)) bits.push('quote ' + s.quote.filter(Boolean).join(' '));
      return 'Preview of your map' + (bits.length ? ': ' + bits.join('; ') : ', no personalization');
    }
    /* a badge map's sharp patch (cfg.badgeDetail): fetched the first time the map zooms in or opens close up, then shown
       in every drawing of it */
    var detailOn = false;
    function detailInto(svg) {
      var d = svg && detailOn && svg.querySelector('[data-mym-detail]');
      if (d && d.getAttribute('href') !== cfg.badgeDetail) d.setAttribute('href', cfg.badgeDetail);
    }
    function wantDetail() {
      if (detailOn || !cfg.badgeDetail) return;
      load(cfg.badgeDetail, function () {
        detailOn = true;
        views.forEach(function (v) { detailInto(v.svg); });
        detailInto(proofSvg);
      });
    }
    function renderView(view, opts) {
      if (!shownNow(view.stage)) { view.dirty = true; return; }
      var svg = view.svg;
      if (svg && svg.__mym.key === state.size + '|' + state.frame) {
        refresh(svg, state, opts);
      } else {
        var keep = svg && svg.querySelector('[data-mym-base]');
        svg = view.svg = build(state, Object.assign({ href: keep ? keep.getAttribute('href') : '' }, opts));
        setKids(view.stage, [svg]);
        if (window.requestAnimationFrame) requestAnimationFrame(stickyTop);   // a frame changes the preview's height
      }
      showBase(view, baseHref(state, opts));
      layout(svg);
      var board = svg.querySelector('[data-mym-board]'), v = zoomed && bandBox(svg, zoomed);
      if (!board.__anim) board.setAttribute('viewBox', (v || [0, 0, svg.__mym.W || MW, svg.__mym.H]).map(function (n) { return n.toFixed(1); }).join(' '));
      view.stage.classList.toggle('is-zoomed', !!v);
      if (v) wantDetail();
      detailInto(svg);
      view.dirty = false;
    }
    function render() {
      recheck();
      holdExpress();                                                   // the frame may have changed
      var opts = { bases: cfg.bases, compass: cfg.compass, badgeArt: cfg.badgeArt, badgeDetail: cfg.badgeDetail, label: ariaLabel() };
      views.forEach(function (v) { renderView(v, opts); });
      var blank = isBlank(shown(state)), text = blank ? 'No personalization. The map fills the canvas.' : 'Live preview · ' + (SIZES[state.size] || SIZES.s20).label;
      $$('[data-mym-caption]').concat(slide ? Array.prototype.slice.call(slide.querySelectorAll('[data-mym-caption]')) : [])
        .forEach(function (c) { c.textContent = c.closest('[data-mym-gallery]') ? (blank ? 'Your map · no personalization' : 'Your map · ' + (SIZES[state.size] || SIZES.s20).label) : text; });
      /* the badge's close-up, once there is one */
      $$('[data-mym-closeup]').concat(slide ? Array.prototype.slice.call(slide.querySelectorAll('[data-mym-closeup]')) : [])
        .forEach(function (b) { b.hidden = blank; });
      if (thumbImg) {
        var b = (cfg.bases || {})[state.style] || {}, src = blank ? b.thumbFull : b.thumb;
        if (src && thumbImg.getAttribute('src') !== src) thumbImg.setAttribute('src', src);
      }
    }
    function drawDirty() {
      var opts = { bases: cfg.bases, compass: cfg.compass, badgeArt: cfg.badgeArt, badgeDetail: cfg.badgeDetail, label: ariaLabel() };
      views.forEach(function (v) { if (v.dirty) renderView(v, opts); });
    }

    /* ── the photo spot: the map takes it as soon as they pick a style or start typing; the photos stay a
          tap away in the thumbnails (and a swipe away on a phone), and typing brings the map back ── */
    function keepPlace() {
      if (!slide || !gallery) return;
      var list = gallery.querySelector('[id^="GalleryViewer"] ul');
      /* Dawn's updateMedia() drops a slide that its fetched HTML lacks: put it back after the first photo */
      if (list && !list.contains(slide)) {
        var first = list.querySelector('[data-media-id]');
        if (first) first.after(slide); else list.prepend(slide);
      }
      var tl = thumb && thumb.parentElement;
      if (tl && tl.firstElementChild !== thumb) tl.prepend(thumb);
    }
    function showMap() {
      if (!slide || !gallery) return;
      keepPlace();
      var list = slide.parentElement;
      if (!slide.classList.contains('is-active')) {
        list.querySelectorAll('[data-media-id]').forEach(function (li) { li.classList.toggle('is-active', li === slide); });
        if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
        var rail = gallery.querySelector('[id^="GalleryThumbnails"]');
        if (rail) rail.querySelectorAll('button').forEach(function (btn) {
          if (thumb && thumb.contains(btn)) btn.setAttribute('aria-current', 'true'); else btn.removeAttribute('aria-current');
        });
      }
      /* on a phone the photos sit side by side: slide the strip over to the map, without moving the page */
      if (!wide.matches) slideStrip(list);
      drawDirty();
    }
    /* The strip rests its scroll-padding short of a slide's edge. A second call while the slide-in is under way
       lets it run; a layout change can snap the strip back to the photo it left, so look again once it should
       have arrived and finish the move, unless they have touched the strip since. */
    var strip = { goal: -1, at: 0, from: 0, timer: 0, touched: 0 };
    function slideStrip(list) {
      var pad = parseFloat(getComputedStyle(list).scrollPaddingLeft) || 0;
      var goal = Math.round(Math.max(0, Math.min(slide.offsetLeft - pad, list.scrollWidth - list.clientWidth)));
      if (Math.abs(list.scrollLeft - goal) < 2) return;
      if (strip.goal === goal && Date.now() - strip.at < 800 && Math.abs(list.scrollLeft - strip.from) >= 1) return;
      strip.goal = goal; strip.at = Date.now(); strip.from = list.scrollLeft;
      list.scrollTo({ left: goal, behavior: reduceMotion ? 'auto' : 'smooth' });
      clearTimeout(strip.timer);
      strip.timer = setTimeout(function () {
        var asked = strip.at; strip.goal = -1;
        if (wide.matches || strip.touched >= asked || !slide.classList.contains('is-active') || slide.parentElement !== list) return;
        if (Math.abs(list.scrollLeft - goal) >= 2) list.scrollTo({ left: goal, behavior: reduceMotion ? 'auto' : 'smooth' });
      }, 800);
    }
    if (gallery) ['touchstart', 'pointerdown', 'wheel'].forEach(function (t) {
      gallery.addEventListener(t, function () { strip.touched = Date.now(); }, { passive: true });
    });
    /* their own click on the thumbnail: Dawn shows the slide, then it is drawn */
    if (thumb) thumb.addEventListener('click', function () { setTimeout(drawDirty, 0); });
    if (gallery) gallery.addEventListener('slideChanged', function () { setTimeout(drawDirty, 0); });
    if (wide.addEventListener) wide.addEventListener('change', function () { setTimeout(function () { drawDirty(); stickyTop(); }, 0); });

    /* ── zoom in on the band being typed in; back out shortly after the typing stops ── */
    var zoomTimer = 0;
    function bandOf(part) { return part === 'title' || part === 'subtitle' ? 'top' : 'bottom'; }
    function zoomTo(band) {
      clearTimeout(zoomTimer);
      if (zoomed === band) return;
      var any = false;
      views.forEach(function (v) {
        if (!v.svg || !shownNow(v.stage)) return;
        var box = bandBox(v.svg, band); if (!box) return;
        any = true;
        v.stage.classList.add('is-zoomed');
        animateView(v.svg.querySelector('[data-mym-board]'), box);
      });
      if (any) zoomed = band;
    }
    function zoomOut(now) {
      clearTimeout(zoomTimer);
      var go = function () {
        if (!zoomed) return;
        zoomed = null;
        views.forEach(function (v) {
          if (!v.svg) return;
          v.stage.classList.remove('is-zoomed');
          animateView(v.svg.querySelector('[data-mym-board]'), [0, 0, v.svg.__mym.W || MW, v.svg.__mym.H]);
        });
      };
      if (now) go(); else zoomTimer = setTimeout(go, 900);
    }
    root.addEventListener('focusin', function (e) {
      var part = e.target.closest('[data-part]');
      if (part && e.target.matches('input[type="text"]')) {
        showMap();
        zoomTo(bandOf(part.getAttribute('data-part')));
      }
      /* Tab can land on a field that sits in view but under the stuck preview: bring it out below */
      var pv = $('.kr-mym__preview');
      if (!e.target.matches('input, textarea, button') || !shownNow(pv) || getComputedStyle(pv).position !== 'sticky') return;
      var pb = pv.getBoundingClientRect().bottom, fr = e.target.getBoundingClientRect();
      if (fr.top < pb + 8 && fr.bottom > 0 && !pv.contains(e.target)) window.scrollBy({ top: fr.top - pb - 16, behavior: reduceMotion ? 'auto' : 'smooth' });
    });
    root.addEventListener('focusout', function () { zoomOut(false); });

    /* ── the larger view. Where the map has the badge (the Muted Tones maps), it also opens close up on the legend (Legend
          close-up, beside View larger), and a switch in it goes between the two (Kevin, 17 Oct 2026: "some sort of option
          to just view the legend close up as well as the view larger option"). ── */
    var proof = null, lastFocus = null, proofSvg = null, proofClose = false;
    var hasCloseup = badgeMap && !!cfg.badgeArtLg;
    function openProof(close) {
      if (!proof) {
        proof = document.createElement('div');
        proof.className = 'kr-mym-proof';
        proof.hidden = true;
        proof.setAttribute('role', 'dialog'); proof.setAttribute('aria-modal', 'true'); proof.setAttribute('aria-label', 'Your map, larger');
        proof.innerHTML = '<div class="kr-mym-proof__inner"><div class="kr-mym-proof__art"></div><p class="kr-mym-proof__cap"></p></div>' +
                          (hasCloseup ? '<button type="button" class="kr-mym-proof__switch" data-mym-proof-switch>Legend close-up</button>' : '') +
                          '<button type="button" class="kr-mym-proof__close">Close</button>';
        document.body.appendChild(proof);
        proof.addEventListener('click', function (e) {
          if (e.target === proof || e.target.closest('.kr-mym-proof__close')) { closeProof(); return; }
          if (e.target.closest('[data-mym-proof-switch]')) { proofClose = !proofClose; proofView(true); }
        });
        document.addEventListener('keydown', function (e) {
          if (!proof || proof.hidden) return;
          if (e.key === 'Escape') closeProof();
          else if (e.key === 'Tab') {                                   // its own buttons only: the switch (if any) and Close
            e.preventDefault();
            var bs = Array.prototype.filter.call(proof.querySelectorAll('button'), function (b) { return !b.hidden; });
            var i = bs.indexOf(document.activeElement), n = bs.length;
            bs[i < 0 ? 0 : (i + (e.shiftKey ? n - 1 : 1)) % n].focus();
          }
        });
      }
      proofClose = !!close && hasCloseup;
      lastFocus = document.activeElement;
      var opts = { bases: cfg.bases, compass: cfg.compass, badgeArt: cfg.badgeArt, badgeDetail: cfg.badgeDetail, large: true, label: ariaLabel() };
      var drawn = views.filter(function (v) { return v.svg; })[0], small = drawn && drawn.svg.querySelector('[data-mym-base]');
      var svg = build(state, Object.assign({ href: small ? small.getAttribute('href') : '' }, opts));
      detailInto(svg);
      var art = proof.querySelector('.kr-mym-proof__art');
      setKids(art, [svg]);
      art.style.setProperty('--mym-ar', String(svg.viewBox.baseVal.width / svg.viewBox.baseVal.height));
      proof.querySelector('.kr-mym-proof__cap').textContent = [(SIZES[state.size] || SIZES.s20).label, state.styleName, framePos ? FRAME_NAMES[state.frame] : ''].filter(Boolean).join(' · ');
      proof.hidden = false;
      document.documentElement.classList.add('kr-mym-locked');
      layout(svg);
      proofSvg = svg;
      proofView(false);
      var hi = baseHref(state, opts), img = svg.querySelector('[data-mym-base]');
      load(hi, function () { img.setAttribute('href', hi); });
      proof.querySelector('.kr-mym-proof__close').focus();
    }
    /* the whole map, or the badge close up (the typing zoom's box) with its art at full size */
    function proofView(glide) {
      var board = proofSvg.querySelector('[data-mym-board]'), sw = proof.querySelector('[data-mym-proof-switch]');
      var some = hasCloseup && !isBlank(shown(state)), close = proofClose && some;
      var box = (close && bandBox(proofSvg, 'bottom')) || [0, 0, proofSvg.__mym.W || MW, proofSvg.__mym.H];
      if (close) wantDetail();
      if (glide && !reduceMotion) animateView(board, box);
      else { if (board.__anim) cancelAnimationFrame(board.__anim); board.__anim = 0; board.setAttribute('viewBox', box.map(function (n) { return n.toFixed(1); }).join(' ')); }
      var artImg = proofSvg.querySelector('[data-mym-art]'), key = badgeLayout(state);
      if (artImg && key) {
        var small = (cfg.badgeArt || {})[key], big = (cfg.badgeArtLg || {})[key];
        if (close && big) load(big, function () { if (proofClose && artImg.isConnected) artImg.setAttribute('href', big); });
        else if (small) artImg.setAttribute('href', small);
      }
      if (sw) { sw.hidden = !some; sw.textContent = close ? 'Whole map' : 'Legend close-up'; }
      proof.classList.toggle('is-close', close);
      proof.setAttribute('aria-label', close ? 'Your map’s legend, close up' : 'Your map, larger');
    }
    function closeProof() {
      proof.hidden = true;
      document.documentElement.classList.remove('kr-mym-locked');
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    /* ── a sticky preview clears a sticky header. The KR header group sticks at top: -38px, so the
          announcement bar slides away and only the rest stays: height + top. ── */
    function stickyTop() {
      var top = 0;
      document.querySelectorAll('.shopify-section-group-header-group, .shopify-section-header-sticky, .section-header').forEach(function (hs) {
        var cs = getComputedStyle(hs);
        if (cs.position !== 'sticky' && cs.position !== 'fixed') return;
        top = Math.max(top, Math.round(hs.getBoundingClientRect().height + (parseFloat(cs.top) || 0)));
      });
      root.style.setProperty('--kr-mym-top', top + 'px');
      /* the gallery sticks at Dawn's 30px, under the 81px header, which would hide the map's title:
         on this page it sticks just below the header instead */
      if (gallery && getComputedStyle(gallery).position === 'sticky') gallery.style.top = (top + 12) + 'px';
      /* a field brought into view (Tab, a tap) lands below the stuck preview, not under it */
      var pv = $('.kr-mym__preview'), stuck = pv && getComputedStyle(pv).position === 'sticky';
      root.style.setProperty('--kr-mym-cover', (stuck ? top + pv.getBoundingClientRect().height + 16 : top + 16) + 'px');
    }
    stickyTop();
    window.addEventListener('resize', stickyTop);

    /* ── go ── */
    syncForm();
    preload();
    render();
    root.classList.add('is-ready');
    if (document.fonts) {
      var relayout = function () { views.forEach(function (v) { if (v.svg && shownNow(v.stage)) layout(v.svg); }); };
      document.fonts.ready.then(relayout);
      if (document.fonts.addEventListener) document.fonts.addEventListener('loadingdone', relayout);
    }
    var api = { state: state, render: render, openProof: openProof, update: update, views: views };
    root.__mym = api;
    return api;
  }

  window.KRMakeYourMap = {
    build: build, layout: layout, refresh: refresh, shown: shown, isBlank: isBlank, STYLES: STYLES, mount: mount,
    /* how the basemap is lifted for a state (also svg.__mym.lift once drawn; null: not at all), in board units
       (12000 across, svg.__mym.H down), for a builder that paints the base itself: draw it, then draw it again
       clipped to x, y, width, height with transform(1, 0, 0, k, 0, fixed * (1 - k)) */
    liftOf: liftOf,
    /* what a style draws with right now: built-in defaults with assets/kr-mym-styles.js laid over them */
    styleFor: function (style) { return { inks: inksFor(style), layout: geo(style) }; }
  };

  function boot() { document.querySelectorAll('[data-kr-mym]').forEach(mount); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
